import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import QRCode from 'qrcode';
import { AlertCircle, Check, Download, Image, Loader2, Maximize2, Upload, X } from 'lucide-react';
import GuideSofiaQrLogo from '../ui/LOGO_guidesofia_QR.jpg';
import ImageUploader from '../ui/ImageUploader';
import DeskDisplayQrBackground from '../../desk-display-QR.jpg';
import DefaultSofiaOverlay from './sofiaoverlay.png';
import { LANDSCAPE_VARIATIONS, landscapeVariation, mediaAssetConfig, mediaAssetCopyId } from './mediaAssetConfig';
import { aiService } from '../../services/aiService';
import { mediaVideoRenderService } from '../../services/mediaVideoRenderService';
import { uploadMediaPackageStoryAsset } from './mediaPackageCloudStore';

export const MEDIA_ASSET_SPECS = [
  { id: 'instagram_feed', label: 'Instagram feed', width: 1080, height: 1350, channel: 'instagram_facebook' },
  { id: 'instagram_story', label: 'Instagram / Facebook Story photos', width: 1080, height: 1920, channel: 'instagram_facebook' },
  { id: 'social_link', label: '16:9 landscape', width: 1920, height: 1080, channel: 'partner_message' },
  { id: 'phone_mockup', label: 'Phone mockup', width: 1080, height: 1350, channel: 'instagram_facebook', phoneMockup: true },
  { id: 'onsite_qr', label: 'On-site QR poster', width: 1240, height: 1754, channel: 'onsite_sign', qr: true },
];

const controlClass = 'w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-100';
const remoteImageDataUrlCache = new Map();
const clamp = (value, minimum, maximum) => Math.min(maximum, Math.max(minimum, value));
export const hexToRgba = (value, alpha, fallback = '#020617') => {
  const hex = String(value || fallback).replace(/^#/, '');
  const normalized = /^[0-9a-fA-F]{6}$/.test(hex) ? hex : fallback.replace(/^#/, '');
  const red = parseInt(normalized.slice(0, 2), 16);
  const green = parseInt(normalized.slice(2, 4), 16);
  const blue = parseInt(normalized.slice(4, 6), 16);
  return `rgba(${red}, ${green}, ${blue}, ${clamp(alpha, 0, 1)})`;
};
const numberOrDefault = (value, fallback) => (
  value !== '' && value !== null && value !== undefined && Number.isFinite(Number(value))
    ? Number(value)
    : fallback
);

const DEFAULT_HEADLINE_Y = {
  instagram_feed: 68,
  instagram_story: 78,
  social_link: 30,
  website_press: 30,
  onsite_qr: 85.5,
};

const DEFAULT_HEADLINE_X = {
  instagram_feed: 7.5,
  instagram_story: 7.5,
  social_link: 46.6,
  website_press: 46.6,
  onsite_qr: 50,
};

const EXTRA_IMAGE_OVERLAY_IDS = new Set(['instagram_feed', 'instagram_story', 'social_link', 'website_press']);
const LARGE_SOCIAL_LOGO_IDS = new Set(['instagram_feed', 'instagram_story']);
const SMALLER_HEADLINE_IDS = new Set(['social_link', 'website_press']);
const defaultLogoSizeFor = (assetId) => LARGE_SOCIAL_LOGO_IDS.has(assetId) ? 10.5 : 8.5;
const minimumLogoSizeFor = (assetId) => LARGE_SOCIAL_LOGO_IDS.has(assetId) ? 10.5 : 5;
const defaultHeadlineSizeFor = (assetId) => SMALLER_HEADLINE_IDS.has(assetId) ? 65 : 100;
const DEFAULT_CTA_POSITION = {
  instagram_feed: { x: 7.5, y: 92 },
  instagram_story: { x: 7.5, y: 92 },
  social_link: { x: 46.6, y: 88 },
  website_press: { x: 46.6, y: 88 },
};

const SafeMarginOverlay = ({ assetId }) => {
  if (!['instagram_feed', 'instagram_story'].includes(assetId)) return null;
  const isStory = assetId === 'instagram_story';
  return (
    <div className="pointer-events-none absolute inset-0 z-[45]" aria-hidden="true">
      <div
        className={`absolute border-2 border-dashed border-amber-300 bg-amber-300/[0.06] shadow-[0_0_0_1px_rgba(15,23,42,0.5)] ${isStory ? 'inset-x-[7.5%] top-[13%] bottom-[18%]' : 'inset-[7.5%]'}`}
      />
      <div className="absolute left-1/2 top-2 -translate-x-1/2 rounded bg-slate-950/75 px-2 py-1 text-[9px] font-semibold uppercase tracking-wide text-amber-200">
        {isStory ? 'Story safe area' : 'Feed safe margin'}
      </div>
    </div>
  );
};
const partnerLogoPaddingFor = (config, logoSize) => (
  logoSize * (clamp(Number(config.phone_partner_logo_padding_percent) || 0, 0, 35) / 100)
);

export const safeFilename = (value) => String(value || 'guide')
  .normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
  .replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');

const loadImage = (source) => new Promise((resolve, reject) => {
  if (!source) return resolve(null);
  const image = new window.Image();
  if (!String(source).startsWith('data:')) image.crossOrigin = 'anonymous';
  image.onload = () => resolve(image);
  image.onerror = () => reject(new Error('The campaign image could not be loaded. Upload a local approved image or use a URL that permits image downloads.'));
  image.src = source;
});

export const loadCanvasImage = async (source) => {
  if (!source) return null;
  try {
    return await loadImage(source);
  } catch (directError) {
    if (String(source).startsWith('data:')) throw directError;
    let safeDataUrl = remoteImageDataUrlCache.get(source);
    if (!safeDataUrl) {
      safeDataUrl = await aiService.getMediaAssetDataUrl(source);
      remoteImageDataUrlCache.set(source, safeDataUrl);
    }
    return loadImage(safeDataUrl);
  }
};

export const ensureHeadlineFont = async () => {
  if (!document.fonts?.load) return;
  try {
    await Promise.all([
      document.fonts.load('400 64px "Playfair Display"'),
      document.fonts.load('700 64px "Playfair Display"'),
      document.fonts.load('700 64px "Roboto"'),
    ]);
  } catch {
    // Canvas rendering continues with the bundled serif fallback.
  }
};

const drawCover = (context, image, width, height, focalX = 50, focalY = 50) => {
  if (!image) return;
  const scale = Math.max(width / image.width, height / image.height);
  const renderedWidth = image.width * scale;
  const renderedHeight = image.height * scale;
  const overflowX = Math.max(0, renderedWidth - width);
  const overflowY = Math.max(0, renderedHeight - height);
  context.drawImage(
    image,
    -(overflowX * (Number(focalX) / 100)),
    -(overflowY * (Number(focalY) / 100)),
    renderedWidth,
    renderedHeight
  );
};

const drawContained = (context, image, x, y, width, height) => {
  if (!image) return;
  const scale = Math.min(width / image.width, height / image.height);
  const renderedWidth = image.width * scale;
  const renderedHeight = image.height * scale;
  context.drawImage(
    image,
    x + ((width - renderedWidth) / 2),
    y + ((height - renderedHeight) / 2),
    renderedWidth,
    renderedHeight
  );
};

const drawExtraImageOverlay = (context, image, config, bounds = null) => {
  if (!image) return;
  const area = bounds || { x: 0, y: 0, width: context.canvas.width, height: context.canvas.height };
  const sizePercent = clamp(Number(config.image_overlay_size_percent) || 35, 5, 120);
  const width = area.width * (sizePercent / 100);
  const height = width * (image.height / image.width);
  const centreX = area.x + (area.width * (clamp(Number(config.image_overlay_x_percent) || 50, 0, 100) / 100));
  const centreY = area.y + (area.height * (clamp(Number(config.image_overlay_y_percent) || 38, 0, 100) / 100));
  context.save();
  context.drawImage(image, centreX - (width / 2), centreY - (height / 2), width, height);
  context.restore();
};

const DEFAULT_PHONE_CORNERS = {
  top_left: { x: 9, y: 5 },
  top_right: { x: 91, y: 5 },
  bottom_right: { x: 91, y: 95 },
  bottom_left: { x: 9, y: 95 },
};

const bilinearPoint = (corners, u, v) => ({
  x: ((1 - u) * (1 - v) * corners.top_left.x)
    + (u * (1 - v) * corners.top_right.x)
    + (u * v * corners.bottom_right.x)
    + ((1 - u) * v * corners.bottom_left.x),
  y: ((1 - u) * (1 - v) * corners.top_left.y)
    + (u * (1 - v) * corners.top_right.y)
    + (u * v * corners.bottom_right.y)
    + ((1 - u) * v * corners.bottom_left.y),
});

const drawMappedTriangle = (context, image, source, destination) => {
  const [s0, s1, s2] = source;
  const [d0, d1, d2] = destination;
  const denominator = (s0.x * (s1.y - s2.y)) + (s1.x * (s2.y - s0.y)) + (s2.x * (s0.y - s1.y));
  if (!denominator) return;
  const a = ((d0.x * (s1.y - s2.y)) + (d1.x * (s2.y - s0.y)) + (d2.x * (s0.y - s1.y))) / denominator;
  const c = ((d0.x * (s2.x - s1.x)) + (d1.x * (s0.x - s2.x)) + (d2.x * (s1.x - s0.x))) / denominator;
  const e = ((d0.x * ((s1.x * s2.y) - (s2.x * s1.y))) + (d1.x * ((s2.x * s0.y) - (s0.x * s2.y))) + (d2.x * ((s0.x * s1.y) - (s1.x * s0.y)))) / denominator;
  const b = ((d0.y * (s1.y - s2.y)) + (d1.y * (s2.y - s0.y)) + (d2.y * (s0.y - s1.y))) / denominator;
  const d = ((d0.y * (s2.x - s1.x)) + (d1.y * (s0.x - s2.x)) + (d2.y * (s1.x - s0.x))) / denominator;
  const f = ((d0.y * ((s1.x * s2.y) - (s2.x * s1.y))) + (d1.y * ((s2.x * s0.y) - (s0.x * s2.y))) + (d2.y * ((s0.x * s1.y) - (s1.x * s0.y)))) / denominator;
  const centre = {
    x: (d0.x + d1.x + d2.x) / 3,
    y: (d0.y + d1.y + d2.y) / 3,
  };
  const expandedDestination = destination.map((point) => {
    const deltaX = point.x - centre.x;
    const deltaY = point.y - centre.y;
    const length = Math.hypot(deltaX, deltaY) || 1;
    const overlap = 1.25;
    return {
      x: point.x + ((deltaX / length) * overlap),
      y: point.y + ((deltaY / length) * overlap),
    };
  });
  context.save();
  context.beginPath();
  context.moveTo(expandedDestination[0].x, expandedDestination[0].y);
  context.lineTo(expandedDestination[1].x, expandedDestination[1].y);
  context.lineTo(expandedDestination[2].x, expandedDestination[2].y);
  context.closePath();
  context.clip();
  context.setTransform(a, b, c, d, e, f);
  context.drawImage(image, 0, 0);
  context.restore();
};

const drawImageInQuad = (context, image, percentCorners, width, height) => {
  const corners = Object.fromEntries(Object.entries(percentCorners).map(([key, point]) => [key, {
    x: width * (clamp(Number(point?.x) || 0, 0, 100) / 100),
    y: height * (clamp(Number(point?.y) || 0, 0, 100) / 100),
  }]));
  const divisions = 12;
  for (let row = 0; row < divisions; row += 1) {
    for (let column = 0; column < divisions; column += 1) {
      const u0 = column / divisions;
      const u1 = (column + 1) / divisions;
      const v0 = row / divisions;
      const v1 = (row + 1) / divisions;
      const s00 = { x: image.width * u0, y: image.height * v0 };
      const s10 = { x: image.width * u1, y: image.height * v0 };
      const s11 = { x: image.width * u1, y: image.height * v1 };
      const s01 = { x: image.width * u0, y: image.height * v1 };
      const d00 = bilinearPoint(corners, u0, v0);
      const d10 = bilinearPoint(corners, u1, v0);
      const d11 = bilinearPoint(corners, u1, v1);
      const d01 = bilinearPoint(corners, u0, v1);
      drawMappedTriangle(context, image, [s00, s10, s11], [d00, d10, d11]);
      drawMappedTriangle(context, image, [s00, s11, s01], [d00, d11, d01]);
    }
  }
};

const roundedImageCanvas = (image, radiusPercent) => {
  const canvas = document.createElement('canvas');
  canvas.width = image.width;
  canvas.height = image.height;
  const context = canvas.getContext('2d');
  const radius = Math.min(canvas.width, canvas.height) * (clamp(Number(radiusPercent) || 0, 0, 25) / 100);
  context.save();
  context.beginPath();
  if (context.roundRect) {
    context.roundRect(0, 0, canvas.width, canvas.height, radius);
  } else {
    context.moveTo(radius, 0);
    context.lineTo(canvas.width - radius, 0);
    context.quadraticCurveTo(canvas.width, 0, canvas.width, radius);
    context.lineTo(canvas.width, canvas.height - radius);
    context.quadraticCurveTo(canvas.width, canvas.height, canvas.width - radius, canvas.height);
    context.lineTo(radius, canvas.height);
    context.quadraticCurveTo(0, canvas.height, 0, canvas.height - radius);
    context.lineTo(0, radius);
    context.quadraticCurveTo(0, 0, radius, 0);
  }
  context.closePath();
  context.clip();
  context.drawImage(image, 0, 0);
  context.restore();
  return canvas;
};

const renderPhoneMockup = (context, spec, config, frameImage, screenImage, partnerLogo, guideSofiaLogo, qrImage) => {
  const primary = config.phone_background_start_color || '#020617';
  const accent = config.phone_background_end_color || '#374151';
  const background = context.createLinearGradient(0, 0, spec.width, spec.height);
  background.addColorStop(0, primary);
  background.addColorStop(1, accent);
  context.fillStyle = background;
  context.fillRect(0, 0, spec.width, spec.height);

  if (!frameImage) return;
  const size = clamp(Number(config.phone_size_percent) || 72, 20, 120) / 100;
  const maximumWidth = spec.width * size;
  const maximumHeight = spec.height * 0.92 * size;
  const frameScale = Math.min(maximumWidth / frameImage.width, maximumHeight / frameImage.height);
  const phoneWidth = frameImage.width * frameScale;
  const phoneHeight = frameImage.height * frameScale;
  const phoneCanvas = document.createElement('canvas');
  phoneCanvas.width = Math.max(1, Math.round(phoneWidth));
  phoneCanvas.height = Math.max(1, Math.round(phoneHeight));
  const phoneContext = phoneCanvas.getContext('2d');

  if (screenImage) {
    const preparedScreen = roundedImageCanvas(screenImage, config.phone_screen_corner_radius_percent);
    drawImageInQuad(
      phoneContext,
      preparedScreen,
      { ...DEFAULT_PHONE_CORNERS, ...(config.phone_screen_corners || {}) },
      phoneCanvas.width,
      phoneCanvas.height
    );
  }
  phoneContext.drawImage(frameImage, 0, 0, phoneCanvas.width, phoneCanvas.height);

  const centreX = spec.width * (clamp(Number(config.phone_x_percent) || 50, 0, 100) / 100);
  const centreY = spec.height * (clamp(Number(config.phone_y_percent) || 52, 0, 100) / 100);
  const angle = clamp(Number(config.phone_tilt_degrees) || 0, -45, 45) * (Math.PI / 180);
  context.save();
  context.translate(centreX, centreY);
  context.rotate(angle);
  context.drawImage(phoneCanvas, -phoneCanvas.width / 2, -phoneCanvas.height / 2);
  context.restore();

  if (config.phone_show_qr && qrImage) {
    const qrSize = spec.width * (clamp(Number(config.phone_qr_size_percent) || 18, 6, 40) / 100);
    const qrPosition = config.phone_qr_position || { x: 13, y: 84 };
    const qrX = spec.width * (clamp(Number(qrPosition.x) || 0, 0, 100) / 100) - (qrSize / 2);
    const qrY = spec.height * (clamp(Number(qrPosition.y) || 0, 0, 100) / 100) - (qrSize / 2);
    context.fillStyle = config.phone_overlay_text_color || '#ffffff';
    context.fillRect(qrX, qrY, qrSize, qrSize);
    context.drawImage(qrImage, qrX, qrY, qrSize, qrSize);
    if (guideSofiaLogo) {
      const centreLogoSize = qrSize * 0.28;
      const centreLogoX = qrX + ((qrSize - centreLogoSize) / 2);
      const centreLogoY = qrY + ((qrSize - centreLogoSize) / 2);
      context.fillStyle = '#ffffff';
      context.fillRect(centreLogoX, centreLogoY, centreLogoSize, centreLogoSize);
      context.drawImage(guideSofiaLogo, centreLogoX, centreLogoY, centreLogoSize, centreLogoSize);
    }
  }

  const logoSize = spec.width * (clamp(Number(config.phone_overlay_logo_size_percent) || 8, 3, 40) / 100);
  const drawPositionedLogo = (image, position, whiteBackground, paddingPercent = 0) => {
    if (!image) return;
    const x = spec.width * (clamp(Number(position?.x) || 0, 0, 100) / 100) - (logoSize / 2);
    const y = spec.height * (clamp(Number(position?.y) || 0, 0, 100) / 100) - (logoSize / 2);
    const padding = logoSize * (clamp(Number(paddingPercent) || 0, 0, 35) / 100);
    drawCircularLogo(context, image, x, y, logoSize, { whiteBackground, padding });
  };
  drawPositionedLogo(guideSofiaLogo, config.phone_guidesofia_logo_position || { x: 91, y: 18 }, true);
  drawPositionedLogo(partnerLogo, config.phone_partner_logo_position || { x: 91, y: 8 }, true, config.phone_partner_logo_padding_percent ?? 12);

  const overlayText = String(config.phone_overlay_text || '').trim();
  if (overlayText) {
    const position = config.phone_overlay_text_position || { x: 7, y: 7 };
    const x = spec.width * (clamp(Number(position.x) || 0, 0, 100) / 100);
    let y = spec.height * (clamp(Number(position.y) || 0, 0, 100) / 100);
    const fontSize = spec.width * (clamp(Number(config.phone_overlay_text_size_percent) || 4.5, 1.5, 12) / 100);
    context.textAlign = 'left';
    context.textBaseline = 'top';
    context.fillStyle = config.phone_overlay_text_color || '#ffffff';
    context.font = `400 ${fontSize}px "Playfair Display", Georgia, serif`;
    const lines = wrapText(context, overlayText, spec.width - x - (spec.width * 0.06)).slice(0, 4);
    lines.forEach((line) => {
      context.fillText(line, x, y);
      y += fontSize * 1.15;
    });
  }

  const subheaderText = String(config.phone_subheader_text || '').trim();
  if (subheaderText) {
    const position = config.phone_subheader_position || { x: 7, y: 16 };
    const x = spec.width * (clamp(Number(position.x) || 0, 0, 100) / 100);
    let y = spec.height * (clamp(Number(position.y) || 0, 0, 100) / 100);
    const fontSize = spec.width * (clamp(Number(config.phone_subheader_text_size_percent) || 2.5, 1, 10) / 100);
    context.textAlign = 'left';
    context.textBaseline = 'top';
    context.fillStyle = config.phone_subheader_text_color || '#d1d5db';
    context.font = `400 ${fontSize}px "Roboto", Arial, sans-serif`;
    const lines = wrapText(context, subheaderText, spec.width - x - (spec.width * 0.06)).slice(0, 5);
    lines.forEach((line) => {
      context.fillText(line, x, y);
      y += fontSize * 1.25;
    });
  }
};

const drawCircularLogo = (context, image, x, y, size, { whiteBackground = false, padding = 0, contentScale = 100 } = {}) => {
  if (!image) return;
  context.save();
  context.beginPath();
  context.arc(x + (size / 2), y + (size / 2), size / 2, 0, Math.PI * 2);
  context.closePath();
  context.clip();
  if (whiteBackground) {
    context.fillStyle = '#ffffff';
    context.fillRect(x, y, size, size);
  }
  const innerSize = size - (padding * 2);
  const scaledSize = innerSize * (clamp(Number(contentScale) || 100, 50, 300) / 100);
  drawContained(
    context,
    image,
    x + padding + ((innerSize - scaledSize) / 2),
    y + padding + ((innerSize - scaledSize) / 2),
    scaledSize,
    scaledSize
  );
  context.restore();
};

const wrapText = (context, text, maxWidth) => {
  const words = String(text || '').trim().split(/\s+/).filter(Boolean);
  const lines = [];
  let line = '';
  words.forEach((word) => {
    const candidate = line ? `${line} ${word}` : word;
    if (context.measureText(candidate).width <= maxWidth || !line) line = candidate;
    else {
      lines.push(line);
      line = word;
    }
  });
  if (line) lines.push(line);
  return lines;
};

const richHeadlineFont = (segment, size) => {
  const weight = segment.fontWeight || '700';
  const style = segment.fontStyle || 'normal';
  const family = String(segment.fontFamily || 'Playfair Display').replace(/["']/g, '');
  return `${style} ${weight} ${size}px "${family}", Georgia, serif`;
};

const drawRichHeadline = (context, segments, fallbackText, x, y, maxWidth, size, lineHeight, maxLines = 4, defaultColor = '#ffffff') => {
  const source = Array.isArray(segments) && segments.length ? segments : [{ text: fallbackText }];
  const tokens = source.flatMap((segment) => String(segment.text || '').split(/(\n|\s+)/).filter(Boolean).map((text) => ({ ...segment, text })));
  const lines = [[]];
  let lineWidth = 0;
  tokens.forEach((token) => {
    if (token.text === '\n') {
      if (lines.length < maxLines) lines.push([]);
      lineWidth = 0;
      return;
    }
    context.font = richHeadlineFont(token, size);
    const width = context.measureText(token.text).width;
    const isSpace = /^\s+$/.test(token.text);
    if (!isSpace && lineWidth > 0 && lineWidth + width > maxWidth && lines.length < maxLines) {
      lines.push([]);
      lineWidth = 0;
    }
    if (!(isSpace && lineWidth === 0)) {
      lines[lines.length - 1].push({ ...token, width });
      lineWidth += width;
    }
  });
  lines.slice(0, maxLines).forEach((line) => {
    let cursorX = x;
    line.forEach((token) => {
      context.font = richHeadlineFont(token, size);
      context.fillStyle = token.color || defaultColor;
      context.fillText(token.text, cursorX, y);
      cursorX += token.width;
    });
    y += lineHeight;
  });
};

const renderWebsitePressHero = (context, spec, config, campaignImage, extraImageOverlay, partnerLogo) => {
  const width = spec.width;
  const height = spec.height;
  const leftWidth = Math.round(width * 0.40);
  const rightX = leftWidth;
  const rightWidth = width - rightX;
  const primary = config.brand_primary_color || '#3b87a9';
  const accent = config.brand_accent_color || '#57bfdc';

  context.fillStyle = primary;
  context.fillRect(0, 0, width, height);
  drawCover(context, campaignImage, width, height, config.visual_image_x, config.visual_image_y);
  const fullBackgroundShade = context.createLinearGradient(0, 0, width, 0);
  fullBackgroundShade.addColorStop(0, hexToRgba(config.visual_gradient_start_color, 0.05));
  fullBackgroundShade.addColorStop(0.55, hexToRgba(config.visual_gradient_start_color, 0.18));
  fullBackgroundShade.addColorStop(1, hexToRgba(config.visual_gradient_end_color, 0.52));
  context.fillStyle = fullBackgroundShade;
  context.fillRect(0, 0, width, height);
  context.save();
  context.beginPath();
  context.rect(0, 0, leftWidth, height);
  context.clip();
  drawExtraImageOverlay(context, extraImageOverlay, config, { x: 0, y: 0, width: leftWidth, height });
  context.restore();

  const padding = Math.round(rightWidth * 0.11);
  const defaultTextX = rightX + padding;
  const headlineX = Math.round(width * (clamp(numberOrDefault(config.visual_headline_x_percent, 46.6), 3, 75) / 100));
  const textMaxWidth = Math.max(width * 0.15, width - headlineX - padding);
  const small = Math.round(width * 0.018);
  context.textBaseline = 'top';
  context.shadowColor = 'rgba(2, 6, 23, 0.82)';
  context.shadowBlur = Math.round(width * 0.009);
  context.shadowOffsetY = Math.round(width * 0.003);
  if (spec.id !== 'website_press' && spec.id !== 'social_link') {
    context.fillStyle = accent;
    context.font = `700 ${small}px "Roboto", Arial, sans-serif`;
    context.fillText(String(config.organisation || '').toUpperCase(), defaultTextX, Math.round(height * 0.095), rightWidth - (padding * 2));
  }

  const headlineSizePercent = clamp(Number(config.visual_headline_size_percent) || 65, 35, 120);
  const titleSize = Math.round(width * 0.052 * (headlineSizePercent / 100));
  const lineHeight = Math.round(titleSize * 1.08);
  const headline = config.visual_headline || config.guide_title || 'Discover the guide';
  let y = Math.round(height * (clamp(Number(config.visual_headline_y_percent) || 30, 18, 72) / 100));
  const overtitle = String(config.visual_overtitle || '').trim();
  if (overtitle) {
    const overtitleSize = Math.round(titleSize * 0.42 * (clamp(Number(config.visual_overtitle_size_percent) || 100, 40, 220) / 100));
    context.fillStyle = config.visual_overtitle_color || accent;
    context.font = `500 ${overtitleSize}px "Roboto", Arial, sans-serif`;
    context.fillText(overtitle, headlineX, y, textMaxWidth);
    y += Math.round(overtitleSize * 1.55);
  }
  context.fillStyle = config.visual_headline_color || '#ffffff';
  context.font = `700 ${titleSize}px "Playfair Display", Georgia, serif`;
  wrapText(context, headline, textMaxWidth).slice(0, 5).forEach((line) => {
    context.fillText(line, headlineX, y, textMaxWidth);
    y += lineHeight;
  });

  const ctaSize = Math.round(small * (clamp(Number(config.visual_cta_size_percent) || 100, 50, 200) / 100));
  const ctaX = Math.round(width * (clamp(numberOrDefault(config.visual_cta_x_percent, 46.6), 1, 95) / 100));
  const ctaY = Math.round(height * (clamp(numberOrDefault(config.visual_cta_y_percent, 88), 2, 96) / 100));
  context.font = `600 ${ctaSize}px "Roboto", Arial, sans-serif`;
  context.fillStyle = config.visual_cta_color || accent;
  context.fillText(config.visual_cta || 'Open the audio guide', ctaX, ctaY, Math.max(width * 0.1, width - ctaX - padding));
  context.shadowColor = 'transparent';
  context.shadowBlur = 0;
  context.shadowOffsetY = 0;

  if (partnerLogo) {
    const logoSizePercent = clamp(Number(config.visual_logo_size_percent) || 8.5, 5, 20);
    const logoSize = Math.round(width * (logoSizePercent / 100));
    const defaultLogoX = 92.5 - (logoSizePercent / 2);
    const defaultLogoY = (7.5 + (logoSizePercent / 2)) * (width / height);
    const centreX = width * (clamp(numberOrDefault(config.visual_partner_logo_x_percent, defaultLogoX), 0, 100) / 100);
    const centreY = height * (clamp(numberOrDefault(config.visual_partner_logo_y_percent, defaultLogoY), 0, 100) / 100);
    drawCircularLogo(context, partnerLogo, centreX - (logoSize / 2), centreY - (logoSize / 2), logoSize, {
      whiteBackground: true,
      padding: partnerLogoPaddingFor(config, logoSize),
    });
  }
};

export const drawStoryVideoOverlay = (context, config, partnerLogo, extraImageOverlay = null, { includeCta = true } = {}) => {
  const width = 1080;
  const height = 1920;
  const accent = config.brand_accent_color || '#57bfdc';
  const margin = Math.round(width * 0.075);

  const overlay = clamp(Number(config.visual_overlay_strength) || 70, 20, 95) / 100;
  const gradient = context.createLinearGradient(0, height * 0.2, 0, height);
  gradient.addColorStop(0, hexToRgba(config.visual_gradient_start_color, 0.05));
  gradient.addColorStop(0.55, hexToRgba(config.visual_gradient_start_color, overlay * 0.7));
  gradient.addColorStop(1, hexToRgba(config.visual_gradient_end_color, Math.min(0.98, overlay + 0.24)));
  context.fillStyle = gradient;
  context.fillRect(0, 0, width, height);
  drawExtraImageOverlay(context, extraImageOverlay, config);

  context.textBaseline = 'top';

  const logoSizePercent = clamp(Number(config.visual_logo_size_percent) || 10.5, 10.5, 20);
  const logoSize = Math.round(width * (logoSizePercent / 100));
  const defaultLogoX = 92.5 - (logoSizePercent / 2);
  const defaultLogoY = (7.5 + (logoSizePercent / 2)) * (width / height);
  const logoCentreX = Math.round(width * (clamp(numberOrDefault(config.visual_partner_logo_x_percent, defaultLogoX), 0, 100) / 100));
  const logoCentreY = Math.round(height * (clamp(numberOrDefault(config.visual_partner_logo_y_percent, defaultLogoY), 0, 100) / 100));
  if (partnerLogo) {
    drawCircularLogo(context, partnerLogo, logoCentreX - (logoSize / 2), logoCentreY - (logoSize / 2), logoSize, {
      whiteBackground: true,
      padding: partnerLogoPaddingFor(config, logoSize),
      contentScale: config.visual_logo_content_scale_percent,
    });
  }

  const titleSize = Math.round(width * 0.083 * (clamp(Number(config.visual_headline_size_percent) || 100, 40, 220) / 100));
  const lineHeight = Math.round(titleSize * 1.08);
  const headlineX = Math.round(width * (clamp(Number(config.visual_headline_x_percent) || 7.5, 3, 75) / 100));
  const headlineMaxWidth = Math.max(width * 0.2, width - headlineX - margin);
  const headline = config.visual_headline || config.guide_title || 'Discover the guide';
  const headlineColor = config.visual_headline_color || '#ffffff';
  context.fillStyle = headlineColor;
  context.font = `700 ${titleSize}px "Playfair Display", Georgia, serif`;
  const hasHeadlineY = config.visual_headline_y_percent !== null
    && config.visual_headline_y_percent !== undefined
    && config.visual_headline_y_percent !== '';
  let y = Math.round(height * ((hasHeadlineY
    ? clamp(Number(config.visual_headline_y_percent) || 0, 5, 88)
    : DEFAULT_HEADLINE_Y.instagram_story) / 100));
  const overtitle = String(config.visual_overtitle || '').trim();
  if (overtitle) {
    const overtitleSizePercent = clamp(Number(config.visual_overtitle_size_percent) || 100, 40, 220);
    const overtitleSize = Math.round(titleSize * 0.42 * (overtitleSizePercent / 100));
    context.font = `700 ${overtitleSize}px "Roboto", Arial, sans-serif`;
    context.fillStyle = config.visual_overtitle_color || accent;
    context.fillText(overtitle, headlineX, y, headlineMaxWidth);
    y += Math.round(overtitleSize * 1.55);
    context.fillStyle = headlineColor;
    context.font = `700 ${titleSize}px "Playfair Display", Georgia, serif`;
  }
  drawRichHeadline(context, config.visual_headline_segments, headline, headlineX, y, headlineMaxWidth, titleSize, lineHeight, 4, headlineColor);

  if (includeCta) {
    const ctaSize = Math.round(width * 0.026 * (clamp(Number(config.visual_cta_size_percent) || 100, 50, 200) / 100));
    const ctaX = Math.round(width * (clamp(numberOrDefault(config.visual_cta_x_percent, 7.5), 1, 95) / 100));
    const ctaY = Math.round(height * (clamp(numberOrDefault(config.visual_cta_y_percent, 92), 2, 96) / 100));
    context.fillStyle = config.visual_cta_color || accent;
    context.font = `700 ${ctaSize}px "Roboto", Arial, sans-serif`;
    context.fillText(config.visual_cta || 'Open the audio guide', ctaX, ctaY, Math.max(width * 0.1, width - ctaX - margin));
  }

};

export const canvasBlob = (canvas) => new Promise((resolve, reject) => {
  canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('The browser could not export this image.'))), 'image/png');
});

const canvasJpegBlob = (canvas) => new Promise((resolve, reject) => {
  canvas.toBlob(
    (blob) => (blob ? resolve(blob) : reject(new Error('The browser could not prepare the Story photo.'))),
    'image/jpeg',
    0.92
  );
});

const compactFingerprint = (value) => {
  const source = String(value || '');
  let hash = 2166136261;
  for (let index = 0; index < source.length; index += 1) {
    hash ^= source.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(36);
};

export const prepareStoryPhoto = async (imageSource, config) => {
  const image = await loadCanvasImage(imageSource);
  if (!image) throw new Error('Add an approved image before rendering a Ken Burns Story video.');
  const canvas = document.createElement('canvas');
  canvas.width = 1440;
  canvas.height = 2560;
  const context = canvas.getContext('2d');
  context.fillStyle = config.brand_primary_color || '#3b87a9';
  context.fillRect(0, 0, canvas.width, canvas.height);
  drawCover(context, image, canvas.width, canvas.height, config.visual_image_x, config.visual_image_y);
  return canvasJpegBlob(canvas);
};

const renderOnsiteQrPoster = async (context, spec, config, posterBackground, partnerLogo, guideSofiaQrLogo) => {
  const width = spec.width;
  const height = spec.height;
  const primary = config.brand_primary_color || '#3b87a9';
  drawCover(context, posterBackground, width, height, 50, 50);

  if (partnerLogo) {
    const logoSizePercent = clamp(Number(config.visual_logo_size_percent) || 26.4, 26.4, 33);
    const logoSize = Math.round(width * (logoSizePercent / 100));
    const logoX = Math.round(width * 0.785) - (logoSize / 2);
    const logoY = Math.round(height * 0.185) - (logoSize / 2);
    drawCircularLogo(context, partnerLogo, logoX, logoY, logoSize, {
      whiteBackground: false,
      padding: partnerLogoPaddingFor(config, logoSize),
    });
  }

  context.textAlign = 'center';
  context.textBaseline = 'top';
  context.fillStyle = config.visual_headline_color || '#0b6570';
  const headlineSizePercent = clamp(
    Number(config.visual_headline_size_percent) || defaultHeadlineSizeFor(spec.id),
    50,
    160
  );
  const posterHeadlineSize = Math.round(width * 0.04 * (headlineSizePercent / 100));
  context.font = `400 ${posterHeadlineSize}px "Roboto", Arial, sans-serif`;
  const titleLines = wrapText(context, config.visual_headline || config.guide_title || 'Audio Guide', width * 0.72).slice(0, 2);
  const titleLineHeight = Math.round(posterHeadlineSize * 1.12);
  const titleX = Math.round(width * (clamp(numberOrDefault(config.visual_headline_x_percent, 50), 3, 97) / 100));
  let titleY = Math.round(height * (clamp(numberOrDefault(config.visual_headline_y_percent, 85.5), 5, 96) / 100));
  titleLines.forEach((line) => {
    context.fillText(line, titleX, titleY);
    titleY += titleLineHeight;
  });

  const qrSize = Math.round(width * 0.37);
  const qrX = Math.round((width - qrSize) / 2);
  const qrY = Math.round(height * 0.57);
  if (config.public_guide_url) {
    const qrDataUrl = await QRCode.toDataURL(config.public_guide_url, {
      width: qrSize,
      margin: 2,
      errorCorrectionLevel: 'H',
      color: { dark: primary, light: '#ffffff' },
    });
    const qrImage = await loadImage(qrDataUrl);
    context.fillStyle = '#ffffff';
    context.fillRect(qrX, qrY, qrSize, qrSize);
    context.drawImage(qrImage, qrX, qrY, qrSize, qrSize);
    const qrLogoSize = Math.round(qrSize * 0.3);
    const qrLogoX = qrX + ((qrSize - qrLogoSize) / 2);
    const qrLogoY = qrY + ((qrSize - qrLogoSize) / 2);
    context.fillStyle = '#ffffff';
    context.fillRect(qrLogoX, qrLogoY, qrLogoSize, qrLogoSize);
    context.drawImage(guideSofiaQrLogo, qrLogoX, qrLogoY, qrLogoSize, qrLogoSize);
  }

};

export const renderAsset = async (canvas, spec, config, imageSource, partnerLogoSource, phoneFrameSource, phoneScreenSource) => {
  await ensureHeadlineFont();
  canvas.width = spec.width;
  canvas.height = spec.height;
  const context = canvas.getContext('2d');
  const primary = config.brand_primary_color || '#3b87a9';
  const accent = config.brand_accent_color || '#57bfdc';
  let campaignImage = null;
  try {
    campaignImage = await loadCanvasImage(config.format_image_url || imageSource);
  } catch {
    campaignImage = null;
  }
  const guideSofiaQrLogo = await loadImage(GuideSofiaQrLogo);
  let partnerLogo = null;
  try {
    partnerLogo = await loadCanvasImage(partnerLogoSource);
  } catch {
    partnerLogo = null;
  }
  let extraImageOverlay = null;
  if (EXTRA_IMAGE_OVERLAY_IDS.has(spec.id) && !config.image_overlay_disabled) {
    try {
      extraImageOverlay = await loadCanvasImage(config.image_overlay_url || DefaultSofiaOverlay);
    } catch {
      extraImageOverlay = null;
    }
  }

  if (spec.id === 'onsite_qr') {
    const posterBackground = await loadImage(DeskDisplayQrBackground);
    await renderOnsiteQrPoster(context, spec, config, posterBackground, partnerLogo, guideSofiaQrLogo);
    return canvasBlob(canvas);
  }

  if (spec.phoneMockup) {
    let phoneFrame = null;
    let phoneScreen = null;
    try { phoneFrame = await loadCanvasImage(phoneFrameSource || config.phone_frame_url); } catch { phoneFrame = null; }
    try { phoneScreen = await loadCanvasImage(phoneScreenSource || config.phone_screen_url); } catch { phoneScreen = null; }
    let phoneQr = null;
    if (config.phone_show_qr && config.public_guide_url) {
      const qrDataUrl = await QRCode.toDataURL(config.public_guide_url, {
        width: 900,
        margin: 2,
        errorCorrectionLevel: 'H',
        color: { dark: '#077691', light: '#ffffff' },
      });
      phoneQr = await loadImage(qrDataUrl);
    }
    renderPhoneMockup(context, spec, config, phoneFrame, phoneScreen, partnerLogo, guideSofiaQrLogo, phoneQr);
    return canvasBlob(canvas);
  }

  if (spec.id === 'website_press' || spec.id === 'social_link') {
    renderWebsitePressHero(context, spec, config, campaignImage, extraImageOverlay, partnerLogo);
    return canvasBlob(canvas);
  }

  context.fillStyle = primary;
  context.fillRect(0, 0, spec.width, spec.height);
  drawCover(context, campaignImage, spec.width, spec.height, config.visual_image_x, config.visual_image_y);

  const gradient = context.createLinearGradient(0, spec.height * 0.2, 0, spec.height);
  const overlay = Math.min(95, Math.max(20, Number(config.visual_overlay_strength) || 70)) / 100;
  gradient.addColorStop(0, hexToRgba(config.visual_gradient_start_color, 0.05));
  gradient.addColorStop(0.55, hexToRgba(config.visual_gradient_start_color, overlay * 0.7));
  gradient.addColorStop(1, hexToRgba(config.visual_gradient_end_color, Math.min(0.98, overlay + 0.24)));
  context.fillStyle = gradient;
  context.fillRect(0, 0, spec.width, spec.height);
  drawExtraImageOverlay(context, extraImageOverlay, config);

  const margin = Math.round(spec.width * 0.075);
  const small = Math.round(spec.width * 0.026);
  const headlineSizePercent = clamp(
    Number(config.visual_headline_size_percent) || defaultHeadlineSizeFor(spec.id),
    20,
    160
  );
  const titleSize = Math.round(
    spec.width * (spec.height > spec.width * 1.45 ? 0.083 : 0.069) * (headlineSizePercent / 100)
  );
  const lineHeight = Math.round(titleSize * 1.08);
  context.textBaseline = 'top';
  context.fillStyle = accent;
  context.font = `700 ${small}px Arial, sans-serif`;
  context.fillText(String(config.organisation || 'ORGANISATION').toUpperCase(), margin, margin, spec.width - (margin * 2));

  const logoSizePercent = clamp(
    Number(config.visual_logo_size_percent) || defaultLogoSizeFor(spec.id),
    minimumLogoSizeFor(spec.id),
    20
  );
  const logoSize = Math.round(spec.width * (logoSizePercent / 100));
  const defaultLogoX = 92.5 - (logoSizePercent / 2);
  const defaultLogoY = (7.5 + (logoSizePercent / 2)) * (spec.width / spec.height);
  const logoCentreX = Math.round(spec.width * (clamp(numberOrDefault(config.visual_partner_logo_x_percent, defaultLogoX), 0, 100) / 100));
  const logoCentreY = Math.round(spec.height * (clamp(numberOrDefault(config.visual_partner_logo_y_percent, defaultLogoY), 0, 100) / 100));
  const logoX = logoCentreX - (logoSize / 2);
  if (partnerLogo) {
    drawCircularLogo(context, partnerLogo, logoX, logoCentreY - (logoSize / 2), logoSize, {
      whiteBackground: true,
      padding: partnerLogoPaddingFor(config, logoSize),
    });
  }

  context.fillStyle = config.visual_headline_color || '#ffffff';
  context.font = `700 ${titleSize}px "Playfair Display", Georgia, serif`;
  const headline = config.visual_headline || config.guide_title || 'Discover the guide';
  const hasHeadlineX = config.visual_headline_x_percent !== null
    && config.visual_headline_x_percent !== undefined
    && config.visual_headline_x_percent !== '';
  const headlineX = hasHeadlineX
    ? Math.round(spec.width * (clamp(Number(config.visual_headline_x_percent) || 0, 3, 75) / 100))
    : margin;
  const reservesLogoColumn = SMALLER_HEADLINE_IDS.has(spec.id);
  const headlineRightEdge = reservesLogoColumn
    ? logoX - Math.round(margin * 0.65)
    : spec.width - margin;
  const headlineMaxWidth = Math.max(spec.width * 0.2, headlineRightEdge - headlineX);
  const headlineLines = wrapText(context, headline, headlineMaxWidth).slice(0, 4);
  const blockHeight = headlineLines.length * lineHeight;
  const qrSpace = spec.qr ? Math.round(spec.width * 0.43) : 0;
  const bottomY = spec.height - margin - blockHeight - Math.round(spec.width * 0.14) - qrSpace;
  const position = config.visual_text_position || 'bottom';
  const hasHeadlineY = config.visual_headline_y_percent !== null
    && config.visual_headline_y_percent !== undefined
    && config.visual_headline_y_percent !== '';
  let y = hasHeadlineY
    ? Math.round(spec.height * (clamp(Number(config.visual_headline_y_percent) || 0, 5, 88) / 100))
    : position === 'top'
      ? Math.round(spec.height * 0.2)
      : position === 'center'
        ? Math.round((spec.height - blockHeight) * 0.5)
        : bottomY;
  if (spec.qr && !hasHeadlineY) y = bottomY;
  headlineLines.forEach((line) => {
    context.fillText(line, headlineX, y, headlineMaxWidth);
    y += lineHeight;
  });

  if (spec.qr && config.public_guide_url) {
    const qrSize = Math.round(spec.width * 0.34);
    const qrDataUrl = await QRCode.toDataURL(config.public_guide_url, { width: qrSize, margin: 2, errorCorrectionLevel: 'H', color: { dark: '#077691', light: '#ffffff' } });
    const qrImage = await loadImage(qrDataUrl);
    context.fillStyle = '#ffffff';
    context.fillRect(margin, spec.height - margin - qrSize, qrSize, qrSize);
    context.drawImage(qrImage, margin, spec.height - margin - qrSize, qrSize, qrSize);
    const qrLogoSize = Math.round(qrSize * 0.34);
    const qrLogoX = margin + ((qrSize - qrLogoSize) / 2);
    const qrLogoY = spec.height - margin - qrSize + ((qrSize - qrLogoSize) / 2);
    context.fillStyle = '#ffffff';
    context.fillRect(qrLogoX, qrLogoY, qrLogoSize, qrLogoSize);
    context.drawImage(guideSofiaQrLogo, qrLogoX, qrLogoY, qrLogoSize, qrLogoSize);
    const ctaSize = Math.round(small * (clamp(Number(config.visual_cta_size_percent) || 100, 50, 200) / 100));
    const ctaX = Math.round(spec.width * (clamp(numberOrDefault(config.visual_cta_x_percent, 45.25), 1, 95) / 100));
    const ctaY = Math.round(spec.height * (clamp(numberOrDefault(config.visual_cta_y_percent, 71), 2, 96) / 100));
    context.font = `700 ${ctaSize}px Arial, sans-serif`;
    context.fillStyle = config.visual_cta_color || '#ffffff';
    context.fillText(config.visual_cta || 'Scan to open the guide', ctaX, ctaY, Math.max(spec.width * 0.1, spec.width - ctaX - margin));
  } else {
    const ctaSize = Math.round(small * (clamp(Number(config.visual_cta_size_percent) || 100, 50, 200) / 100));
    const ctaX = Math.round(spec.width * (clamp(numberOrDefault(config.visual_cta_x_percent, 7.5), 1, 95) / 100));
    const ctaY = Math.round(spec.height * (clamp(numberOrDefault(config.visual_cta_y_percent, 92), 2, 96) / 100));
    context.font = `700 ${ctaSize}px Arial, sans-serif`;
    context.fillStyle = config.visual_cta_color || accent;
    context.fillText(config.visual_cta || 'Open the audio guide', ctaX, ctaY, Math.max(spec.width * 0.1, spec.width - ctaX - margin));
  }

  return canvasBlob(canvas);
};

export const triggerDownload = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
};

const PhonePerspectiveOverlay = ({ config, frameSource, onChange, onPositionChange, isEditing }) => {
  const overlayRef = useRef(null);
  const [frameAspect, setFrameAspect] = useState(0.5);
  const [dragging, setDragging] = useState('');
  const corners = { ...DEFAULT_PHONE_CORNERS, ...(config.phone_screen_corners || {}) };
  const size = clamp(Number(config.phone_size_percent) || 72, 20, 120) / 100;
  const maximumWidth = 1080 * size;
  const maximumHeight = 1350 * 0.92 * size;
  const phoneWidth = Math.min(maximumWidth, maximumHeight * frameAspect);
  const phoneHeight = phoneWidth / frameAspect;
  const centreX = 1080 * (clamp(Number(config.phone_x_percent) || 50, 0, 100) / 100);
  const centreY = 1350 * (clamp(Number(config.phone_y_percent) || 52, 0, 100) / 100);
  const angle = clamp(Number(config.phone_tilt_degrees) || 0, -45, 45) * (Math.PI / 180);
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  const toOutput = (point) => {
    const localX = (point.x / 100 * phoneWidth) - (phoneWidth / 2);
    const localY = (point.y / 100 * phoneHeight) - (phoneHeight / 2);
    return {
      x: centreX + (cos * localX) - (sin * localY),
      y: centreY + (sin * localX) + (cos * localY),
    };
  };
  const outputCorners = Object.fromEntries(Object.entries(corners).map(([key, point]) => [key, toOutput(point)]));
  const moveCorner = (key, event) => {
    if (dragging !== `corner:${key}` || !overlayRef.current) return;
    const bounds = overlayRef.current.getBoundingClientRect();
    const outputX = ((event.clientX - bounds.left) / bounds.width) * 1080;
    const outputY = ((event.clientY - bounds.top) / bounds.height) * 1350;
    const deltaX = outputX - centreX;
    const deltaY = outputY - centreY;
    const localX = (cos * deltaX) + (sin * deltaY) + (phoneWidth / 2);
    const localY = (-sin * deltaX) + (cos * deltaY) + (phoneHeight / 2);
    onChange({
      ...corners,
      [key]: {
        x: Number(clamp((localX / phoneWidth) * 100, 0, 100).toFixed(2)),
        y: Number(clamp((localY / phoneHeight) * 100, 0, 100).toFixed(2)),
      },
    });
  };
  const moveElement = (key, event) => {
    if (dragging !== `element:${key}` || !overlayRef.current) return;
    const bounds = overlayRef.current.getBoundingClientRect();
    onPositionChange(key, {
      x: Number(clamp(((event.clientX - bounds.left) / bounds.width) * 100, 0, 100).toFixed(2)),
      y: Number(clamp(((event.clientY - bounds.top) / bounds.height) * 100, 0, 100).toFixed(2)),
    });
  };
  const positionedElements = [
    { key: 'phone_partner_logo_position', label: 'Partner logo', shortLabel: 'P', position: config.phone_partner_logo_position || { x: 91, y: 8 }, round: true },
    { key: 'phone_guidesofia_logo_position', label: 'GuideSofia logo', shortLabel: 'GS', position: config.phone_guidesofia_logo_position || { x: 91, y: 18 }, round: true },
    ...(String(config.phone_overlay_text || '').trim()
      ? [{ key: 'phone_overlay_text_position', label: `Title: ${config.phone_overlay_text}`, shortLabel: config.phone_overlay_text, position: config.phone_overlay_text_position || { x: 7, y: 7 }, round: false, font: 'title' }]
      : []),
    ...(String(config.phone_subheader_text || '').trim()
      ? [{ key: 'phone_subheader_position', label: `Subheader: ${config.phone_subheader_text}`, shortLabel: config.phone_subheader_text, position: config.phone_subheader_position || { x: 7, y: 16 }, round: false, font: 'subheader' }]
      : []),
    ...(config.phone_show_qr
      ? [{ key: 'phone_qr_position', label: 'Guide QR code', shortLabel: 'QR', position: config.phone_qr_position || { x: 13, y: 84 }, round: false }]
      : []),
  ];

  return (
    <div ref={overlayRef} className="pointer-events-none absolute inset-0">
      {frameSource && <img src={frameSource} alt="" className="hidden" onLoad={(event) => setFrameAspect(event.currentTarget.naturalWidth / event.currentTarget.naturalHeight || 0.5)} />}
      {isEditing && <svg className="absolute inset-0 h-full w-full overflow-visible" viewBox="0 0 1080 1350" aria-hidden="true">
        <polygon
          points={['top_left', 'top_right', 'bottom_right', 'bottom_left'].map((key) => `${outputCorners[key].x},${outputCorners[key].y}`).join(' ')}
          fill="rgba(34,211,238,0.12)"
          stroke="#22d3ee"
          strokeWidth="4"
          strokeDasharray="12 8"
        />
      </svg>}
      {isEditing && Object.entries(outputCorners).map(([key, point]) => (
        <button
          key={key}
          type="button"
          aria-label={`Move ${key.replace('_', ' ')} screen corner`}
          className="pointer-events-auto absolute h-5 w-5 -translate-x-1/2 -translate-y-1/2 cursor-move rounded-full border-2 border-white bg-cyan-500 shadow-lg ring-2 ring-slate-950/60"
          style={{ left: `${(point.x / 1080) * 100}%`, top: `${(point.y / 1350) * 100}%` }}
          onPointerDown={(event) => {
            event.currentTarget.setPointerCapture(event.pointerId);
            setDragging(`corner:${key}`);
          }}
          onPointerMove={(event) => moveCorner(key, event)}
          onPointerUp={() => setDragging('')}
          onPointerCancel={() => setDragging('')}
        />
      ))}
      {isEditing && positionedElements.map((item) => (
        <button
          key={item.key}
          type="button"
          aria-label={`Move ${item.label}`}
          className={`pointer-events-auto absolute -translate-x-1/2 -translate-y-1/2 cursor-move border-2 border-fuchsia-300 bg-fuchsia-950/45 text-[10px] font-semibold text-white shadow-lg ${item.round ? 'flex h-9 w-9 items-center justify-center rounded-full p-0' : item.key === 'phone_qr_position' ? 'flex h-12 w-12 items-center justify-center rounded-md p-0' : 'max-w-[55%] rounded-md px-3 py-2 font-serif text-sm font-normal'}`}
          style={{
            left: `${item.position.x}%`,
            top: `${item.position.y}%`,
            ...(item.round ? {} : {
              fontFamily: item.font === 'subheader'
                ? "'Roboto', Arial, sans-serif"
                : "'Playfair Display', Georgia, serif",
            }),
          }}
          onPointerDown={(event) => {
            event.currentTarget.setPointerCapture(event.pointerId);
            setDragging(`element:${item.key}`);
          }}
          onPointerMove={(event) => moveElement(item.key, event)}
          onPointerUp={() => setDragging('')}
          onPointerCancel={() => setDragging('')}
        >
          {item.shortLabel}
        </button>
      ))}
    </div>
  );
};

const MediaAssetStudio = ({
  storyEditor,
  packageData,
  onConfigChange,
  onAssetsChange,
  onError,
  localCampaignImage,
  onLocalCampaignImageChange,
  localPartnerLogo,
  onLocalPartnerLogoChange,
  localPhoneFrame,
  onLocalPhoneFrameChange,
  localPhoneScreen,
  onLocalPhoneScreenChange,
  workspaceId,
  storyVideoUrl,
  storyVideoFile,
  storyVideoStoragePath,
  storyVideoFingerprint,
  storyPhotoUrl,
  storyPhotoFile,
  storyAudioUrl,
  storyAudioLabel,
  onSaveLayoutDefault,
  onApplyLayoutDefault,
  hasLayoutDefault,
  layoutPreferenceStatus,
}) => {
  const [assets, setAssets] = useState({});
  const [isRendering, setIsRendering] = useState(false);
  const [renderingAssetIds, setRenderingAssetIds] = useState([]);
  const [largePreviewUrl, setLargePreviewUrl] = useState('');
  const [maximizedAssetId, setMaximizedAssetId] = useState('');
  const [maximizedPreviewUrl, setMaximizedPreviewUrl] = useState('');
  const [maximizedViewport, setMaximizedViewport] = useState({
    width: 0,
    height: 0,
    offsetLeft: 0,
    offsetTop: 0,
    headerHeight: 0,
  });
  const [isRenderingStoryVideo, setIsRenderingStoryVideo] = useState(false);
  const [storyVideoProgress, setStoryVideoProgress] = useState(0);
  const [isUploadingFormatOverlay, setIsUploadingFormatOverlay] = useState(false);
  const [formatOverlayUploadProgress, setFormatOverlayUploadProgress] = useState(0);
  const [activeAssetId, setActiveAssetId] = useState(MEDIA_ASSET_SPECS[0].id);
  const [largePreviewInteraction, setLargePreviewInteraction] = useState('');
  const [selectedTextLayer, setSelectedTextLayer] = useState(0);
  const lastLivePreviewRef = useRef('');
  const previewConfigSignature = JSON.stringify(packageData.config);
  const [phonePositionsEditing, setPhonePositionsEditing] = useState(true);
  const [phonePositionsSaved, setPhonePositionsSaved] = useState(false);
  const [assetUploadFeedback, setAssetUploadFeedback] = useState(null);
  const canvasRefs = useRef({});
  const largePreviewRef = useRef(null);
  const maximizedHeaderRef = useRef(null);
  const largePreviewInteractionRef = useRef({ type: '' });
  const initialPreviewRenderRef = useRef(false);
  const selectedLandscapeVariation = landscapeVariation(packageData.config);
  const renderedLandscapeVariationRef = useRef(selectedLandscapeVariation);
  const pendingSourceRenderRef = useRef(null);
  const imageSource = localCampaignImage || packageData.config.campaign_image_url || packageData.guide.thumbnail_url;
  const storyImageSource = storyPhotoUrl || packageData.config.asset_copy?.instagram_story?.format_image_url || imageSource;
  const partnerLogoSource = localPartnerLogo || packageData.config.partner_logo_url;
  const phoneFrameSource = localPhoneFrame || packageData.config.phone_frame_url;
  const phoneScreenSource = localPhoneScreen || packageData.config.phone_screen_url;
  const config = packageData.config;
  const updateSocialMentions = (value) => {
    const mandatory = ['@guide.sofia', config.partner_social_handle].filter(Boolean);
    const extras = String(value || '').split(/\s+/).filter((item) => (
      item.startsWith('@') && !mandatory.includes(item)
    ));
    onConfigChange('social_mentions', [...mandatory, ...extras].join(' '));
  };

  const generateSpecs = useCallback(async (specs) => {
    setIsRendering(true);
    setRenderingAssetIds(specs.map((spec) => spec.id));
    try {
      const next = {};
      for (const spec of specs) {
        if (spec.id === 'instagram_story' && storyVideoUrl) continue;
        const canvas = canvasRefs.current[spec.id];
        const formatConfig = mediaAssetConfig(config, spec.id);
        next[spec.id] = await renderAsset(canvas, spec, formatConfig, imageSource, partnerLogoSource, phoneFrameSource, phoneScreenSource);
      }
      setAssets((current) => {
        const merged = { ...current, ...next };
        onAssetsChange?.(merged);
        return merged;
      });
      return true;
    } catch (error) {
      onError?.(error.message);
      return false;
    } finally {
      setIsRendering(false);
      setRenderingAssetIds([]);
    }
  }, [config, imageSource, partnerLogoSource, phoneFrameSource, phoneScreenSource, storyVideoUrl, onAssetsChange, onError]);

  useEffect(() => {
    if (activeAssetId === 'story_series' || isRendering || largePreviewInteraction) return undefined;
    const signature = JSON.stringify([activeAssetId, previewConfigSignature, imageSource, partnerLogoSource, phoneFrameSource, phoneScreenSource, storyVideoUrl]);
    if (lastLivePreviewRef.current === signature) return undefined;
    const timer = window.setTimeout(() => {
      lastLivePreviewRef.current = signature;
      generateSpecs(MEDIA_ASSET_SPECS.filter((spec) => spec.id === activeAssetId));
    }, 250);
    return () => window.clearTimeout(timer);
  }, [activeAssetId, previewConfigSignature, imageSource, partnerLogoSource, phoneFrameSource, phoneScreenSource, storyVideoUrl, isRendering, largePreviewInteraction, generateSpecs]);

  const generateAll = useCallback(() => generateSpecs(MEDIA_ASSET_SPECS), [generateSpecs]);

  useEffect(() => {
    if (initialPreviewRenderRef.current) return;
    initialPreviewRenderRef.current = true;
    generateAll();
  }, [generateAll]);

  useEffect(() => {
    if (isRendering || renderedLandscapeVariationRef.current === selectedLandscapeVariation) return;
    renderedLandscapeVariationRef.current = selectedLandscapeVariation;
    generateSpecs(MEDIA_ASSET_SPECS.filter((spec) => spec.id === 'social_link'));
  }, [generateSpecs, isRendering, selectedLandscapeVariation]);

  useEffect(() => {
    const pending = pendingSourceRenderRef.current;
    if (!pending) return undefined;
    pendingSourceRenderRef.current = null;
    const timer = window.setTimeout(async () => {
      setAssetUploadFeedback({ tone: 'working', text: `${pending.label} selected. Updating the preview...` });
      const specs = pending.scope === 'phone'
        ? MEDIA_ASSET_SPECS.filter((spec) => spec.id === 'phone_mockup')
        : MEDIA_ASSET_SPECS;
      const rendered = await generateSpecs(specs);
      setAssetUploadFeedback(rendered === false
        ? { tone: 'error', text: `${pending.label} was selected, but its preview could not be generated.` }
        : { tone: 'success', text: `${pending.label} uploaded and preview updated. Firebase autosave will preserve it.` });
    }, 80);
    return () => window.clearTimeout(timer);
  }, [generateSpecs, imageSource, partnerLogoSource, phoneFrameSource, phoneScreenSource]);

  useEffect(() => {
    const blob = assets[activeAssetId];
    if (!blob) {
      setLargePreviewUrl('');
      return undefined;
    }
    const url = URL.createObjectURL(blob);
    setLargePreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [activeAssetId, assets]);

  useEffect(() => {
    if (!maximizedAssetId || (maximizedAssetId === 'instagram_story' && storyVideoUrl)) {
      setMaximizedPreviewUrl('');
      return undefined;
    }
    const blob = assets[maximizedAssetId];
    if (!blob) {
      setMaximizedPreviewUrl('');
      return undefined;
    }
    const url = URL.createObjectURL(blob);
    setMaximizedPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [assets, maximizedAssetId, storyVideoUrl]);

  useEffect(() => {
    if (!maximizedAssetId) return undefined;
    const previousOverflow = document.body.style.overflow;
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') setMaximizedAssetId('');
    };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', closeOnEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', closeOnEscape);
    };
  }, [maximizedAssetId]);

  useEffect(() => {
    if (!maximizedAssetId) return undefined;
    const visualViewport = window.visualViewport;
    const updateViewport = () => {
      setMaximizedViewport({
        width: Math.floor(visualViewport?.width || window.innerWidth || 0),
        height: Math.floor(visualViewport?.height || window.innerHeight || 0),
        offsetLeft: Math.floor(visualViewport?.offsetLeft || 0),
        offsetTop: Math.floor(visualViewport?.offsetTop || 0),
        headerHeight: Math.ceil(maximizedHeaderRef.current?.getBoundingClientRect().height || 64),
      });
    };
    updateViewport();
    const frame = window.requestAnimationFrame(updateViewport);
    const headerObserver = typeof ResizeObserver === 'undefined'
      ? null
      : new ResizeObserver(updateViewport);
    if (maximizedHeaderRef.current) headerObserver?.observe(maximizedHeaderRef.current);
    window.addEventListener('resize', updateViewport);
    visualViewport?.addEventListener('resize', updateViewport);
    visualViewport?.addEventListener('scroll', updateViewport);
    return () => {
      window.cancelAnimationFrame(frame);
      headerObserver?.disconnect();
      window.removeEventListener('resize', updateViewport);
      visualViewport?.removeEventListener('resize', updateViewport);
      visualViewport?.removeEventListener('scroll', updateViewport);
    };
  }, [maximizedAssetId]);

  const handleCampaignImageSelected = (url, attributionData = null) => {
    if (url) {
      pendingSourceRenderRef.current = { scope: 'all', label: 'Campaign image' };
      setAssetUploadFeedback({ tone: 'working', text: 'Campaign image uploaded. Updating previews...' });
    }
    onLocalCampaignImageChange?.('');
    onConfigChange('campaign_image_url', url || '');
    onConfigChange('campaign_image_attribution', attributionData || null);
  };

  const readLocalImage = (event, onChange, { label, scope = 'all' }) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      const text = `Choose a valid image for the ${label}.`;
      setAssetUploadFeedback({ tone: 'error', text });
      onError?.(text);
      event.target.value = '';
      return;
    }
    if (file.size > 20 * 1024 * 1024) {
      const text = `The ${label} is larger than 20 MB. Choose a smaller image.`;
      setAssetUploadFeedback({ tone: 'error', text });
      onError?.(text);
      event.target.value = '';
      return;
    }
    setAssetUploadFeedback({ tone: 'working', text: `Reading ${file.name}...` });
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = String(reader.result || '');
      if (!dataUrl.startsWith('data:image/')) {
        const text = `The ${label} could not be read as an image.`;
        setAssetUploadFeedback({ tone: 'error', text });
        onError?.(text);
        return;
      }
      pendingSourceRenderRef.current = { scope, label };
      onChange?.(dataUrl);
    };
    reader.onerror = () => {
      const text = `The ${label} could not be read. Try the file again.`;
      setAssetUploadFeedback({ tone: 'error', text });
      onError?.(text);
    };
    reader.onabort = () => setAssetUploadFeedback({ tone: 'error', text: `${label} selection was cancelled.` });
    reader.readAsDataURL(file);
    event.target.value = '';
  };

  const handlePartnerLogoUpload = (event) => readLocalImage(event, onLocalPartnerLogoChange, {
    label: 'Partner logo',
  });

  const handleLayerUpload = (event, onChange, label) => {
    readLocalImage(event, onChange, { label, scope: 'phone' });
  };

  const renderAndDownloadStoryVideo = async () => {
    if (isRenderingStoryVideo) return;
    const sourceKind = storyVideoFile || storyVideoStoragePath ? 'video' : 'image';
    if (sourceKind === 'video' && storyVideoFile && !storyVideoFile.type?.startsWith('video/')) {
      onError?.('Choose a valid video before rendering the Instagram Story.');
      return;
    }
    if (sourceKind === 'image' && !storyImageSource) {
      onError?.('Add an approved image before rendering a Ken Burns Story video.');
      return;
    }

    setIsRenderingStoryVideo(true);
    setStoryVideoProgress(0);
    try {
      await ensureHeadlineFont();
      let partnerLogo = null;
      try { partnerLogo = await loadCanvasImage(partnerLogoSource); } catch { partnerLogo = null; }
      const formatConfig = { ...config, ...(config.asset_copy?.instagram_story || {}) };
      let extraImageOverlay = null;
      if (!formatConfig.image_overlay_disabled) {
        try { extraImageOverlay = await loadCanvasImage(formatConfig.image_overlay_url || DefaultSofiaOverlay); } catch { extraImageOverlay = null; }
      }
      const overlayCanvas = document.createElement('canvas');
      overlayCanvas.width = 1080;
      overlayCanvas.height = 1920;
      drawStoryVideoOverlay(overlayCanvas.getContext('2d'), formatConfig, partnerLogo, extraImageOverlay);
      const overlayBlob = await canvasBlob(overlayCanvas);
      const guideKey = `${packageData.guide.guide_id || config.guide_title || 'guide'}_${config.output_language || 'en'}`;
      const sourceFile = sourceKind === 'video'
        ? storyVideoFile || null
        : await prepareStoryPhoto(storyImageSource, formatConfig);
      const sourceFingerprint = sourceKind === 'video'
        ? storyVideoFile
          ? `video:${storyVideoFile.name}:${storyVideoFile.size}:${storyVideoFile.lastModified}`
          : storyVideoFingerprint || `video:cloud:${storyVideoStoragePath}`
        : `image:${storyPhotoFile
          ? `${storyPhotoFile.name}:${storyPhotoFile.size}:${storyPhotoFile.lastModified}`
          : compactFingerprint(storyImageSource)}:${sourceFile.size}:${formatConfig.visual_image_x || 50}:${formatConfig.visual_image_y || 50}`;
      const existingSourcePath = sourceKind === 'video' && storyVideoStoragePath
        ? storyVideoStoragePath
        : config.instagram_story_source_kind === sourceKind
          ? config.instagram_story_source_storage_path
          : '';
      const uploaded = await mediaVideoRenderService.uploadInputs({
        guideKey,
        workspaceId,
        sourceFile,
        sourceKind,
        overlayBlob,
        existingSourcePath,
        sourceFingerprint,
        storedSourceFingerprint: config.instagram_story_source_fingerprint,
        onProgress: setStoryVideoProgress,
      });
      onConfigChange('instagram_story_source_storage_path', uploaded.sourceMediaPath);
      onConfigChange('instagram_story_source_fingerprint', sourceFingerprint);
      onConfigChange('instagram_story_source_kind', sourceKind);

      const filenameBase = `${safeFilename(config.guide_title)}_${safeFilename(storyAudioLabel || 'instagram_story')}`;
      const result = await mediaVideoRenderService.render({
        ...uploaded,
        sourceKind,
        workspaceId,
        audioUrl: storyAudioUrl,
        guideKey,
        outputFilename: filenameBase,
        durationSeconds: Number(config.video_duration_seconds) || 15,
        kenBurnsZoom: Number(config.ken_burns_zoom) || 1.15,
        kenBurnsDirection: config.ken_burns_direction || 'center',
        onProgress: setStoryVideoProgress,
      });
      onConfigChange('instagram_story_last_render_url', result.videoUrl);
      onConfigChange('instagram_story_last_render_storage_path', result.storagePath || '');
      const response = await fetch(result.videoUrl);
      if (!response.ok) throw new Error('The rendered MP4 was created but could not be downloaded.');
      triggerDownload(await response.blob(), `${filenameBase}.mp4`);
      setStoryVideoProgress(100);
    } catch (error) {
      onError?.(error.message || 'The Story video could not be rendered.');
    } finally {
      setIsRenderingStoryVideo(false);
    }
  };

  const activeCopyId = mediaAssetCopyId(config, activeAssetId);
  const activeCopyOverrides = config.asset_copy?.[activeCopyId]
    || (activeAssetId === 'social_link'
      ? config.asset_copy?.[activeCopyId === 'social_link' ? 'website_press' : 'social_link']
      : null)
    || {};
  const activeCopy = {
    visual_headline: config.visual_headline,
    visual_cta: config.visual_cta,
    image_alt_text: config.image_alt_text,
    ...activeCopyOverrides,
  };
  const activeSpec = MEDIA_ASSET_SPECS.find((spec) => spec.id === activeAssetId) || MEDIA_ASSET_SPECS[0];
  const updateActivePreview = () => generateSpecs([activeSpec]);
  const maximizedSpec = MEDIA_ASSET_SPECS.find((spec) => spec.id === maximizedAssetId) || null;
  const maximizedPreviewSize = useMemo(() => {
    if (!maximizedSpec || !maximizedViewport.width || !maximizedViewport.height) return null;
    const totalPadding = maximizedViewport.width >= 640 ? 40 : 24;
    const availableWidth = Math.max(1, maximizedViewport.width - totalPadding);
    const availableHeight = Math.max(1, maximizedViewport.height - maximizedViewport.headerHeight - totalPadding);
    const scale = Math.min(
      availableWidth / maximizedSpec.width,
      availableHeight / maximizedSpec.height
    );
    return {
      width: Math.max(1, Math.floor(maximizedSpec.width * scale)),
      height: Math.max(1, Math.floor(maximizedSpec.height * scale)),
    };
  }, [maximizedSpec, maximizedViewport]);
  const activeHeadlineSize = Number(activeCopy.visual_headline_size_percent) || defaultHeadlineSizeFor(activeSpec.id);
  const activeHeadlineX = activeCopy.visual_headline_x_percent ?? DEFAULT_HEADLINE_X[activeSpec.id] ?? 7.5;
  const activeHeadlineY = activeCopy.visual_headline_y_percent ?? DEFAULT_HEADLINE_Y[activeSpec.id] ?? 60;
  const activeCtaSize = clamp(Number(activeCopy.visual_cta_size_percent) || 100, 50, 200);
  const activeCtaX = clamp(numberOrDefault(activeCopy.visual_cta_x_percent, DEFAULT_CTA_POSITION[activeSpec.id]?.x ?? 7.5), 1, 95);
  const activeCtaY = clamp(numberOrDefault(activeCopy.visual_cta_y_percent, DEFAULT_CTA_POSITION[activeSpec.id]?.y ?? 92), 2, 96);
  const activeHeadlineColor = activeCopy.visual_headline_color || '#ffffff';
  const activeCtaColor = activeCopy.visual_cta_color || config.brand_accent_color || '#57bfdc';
  const activeHeadlineMinimum = activeAssetId === 'instagram_story' ? 40 : activeAssetId === 'onsite_qr' ? 50 : SMALLER_HEADLINE_IDS.has(activeAssetId) ? 35 : 20;
  const activeHeadlineMaximum = activeAssetId === 'instagram_story' ? 220 : activeAssetId === 'onsite_qr' ? 160 : SMALLER_HEADLINE_IDS.has(activeAssetId) ? 120 : 160;
  const activeCtaBoxHeight = clamp(
    (((SMALLER_HEADLINE_IDS.has(activeSpec.id) ? 0.018 : 0.026) * activeSpec.width * (activeCtaSize / 100)) / activeSpec.height) * 140,
    4,
    18
  );
  const activeImageX = clamp(Number(activeCopy.visual_image_x ?? config.visual_image_x ?? 50), 0, 100);
  const activeImageY = clamp(Number(activeCopy.visual_image_y ?? config.visual_image_y ?? 50), 0, 100);
  const activeOverlayStrength = clamp(Number(activeCopy.visual_overlay_strength ?? config.visual_overlay_strength ?? 70), 20, 95);
  const activeSupportsExtraOverlay = EXTRA_IMAGE_OVERLAY_IDS.has(activeSpec.id);
  const activeOverlayEnabled = activeSupportsExtraOverlay && !activeCopy.image_overlay_disabled;
  const activeOverlaySize = clamp(Number(activeCopy.image_overlay_size_percent) || 35, 5, 120);
  const activeOverlayX = clamp(Number(activeCopy.image_overlay_x_percent) || 50, 0, 100);
  const activeOverlayY = clamp(Number(activeCopy.image_overlay_y_percent) || 38, 0, 100);
  const activeLogoSize = activeSpec.id === 'onsite_qr'
    ? clamp(Number(activeCopy.visual_logo_size_percent) || 26.4, 26.4, 33)
    : clamp(
      Number(activeCopy.visual_logo_size_percent) || defaultLogoSizeFor(activeSpec.id),
      minimumLogoSizeFor(activeSpec.id),
      20
    );
  const activeAllowsFreePartnerLogo = activeSpec.id !== 'phone_mockup' && activeSpec.id !== 'onsite_qr';
  const defaultActiveLogoX = 92.5 - (activeLogoSize / 2);
  const defaultActiveLogoY = (7.5 + (activeLogoSize / 2)) * (activeSpec.width / activeSpec.height);
  const activePartnerLogoX = clamp(numberOrDefault(activeCopy.visual_partner_logo_x_percent, defaultActiveLogoX), 0, 100);
  const activePartnerLogoY = clamp(numberOrDefault(activeCopy.visual_partner_logo_y_percent, defaultActiveLogoY), 0, 100);
  const updateActiveCopy = (key, value) => onConfigChange('asset_copy', {
    ...(config.asset_copy || {}),
    [activeCopyId]: {
      ...activeCopyOverrides,
      [key]: value,
    },
  });
  const updateActiveCopyValues = (values) => onConfigChange('asset_copy', {
    ...(config.asset_copy || {}),
    [activeCopyId]: {
      ...activeCopyOverrides,
      ...values,
    },
  });
  const startLargePreviewInteraction = (event, type) => {
    if (type.startsWith('headline')) setSelectedTextLayer(0);
    if (type.startsWith('cta')) setSelectedTextLayer(1);
    event.preventDefault();
    event.stopPropagation();
    event.currentTarget.setPointerCapture?.(event.pointerId);
    largePreviewInteractionRef.current = {
      type,
      startClientX: event.clientX,
      startHeadlineSize: activeHeadlineSize,
      startCtaSize: activeCtaSize,
      startOverlaySize: activeOverlaySize,
    };
    setLargePreviewInteraction(type);
  };
  const stopLargePreviewInteraction = () => {
    largePreviewInteractionRef.current = { type: '' };
    setLargePreviewInteraction('');
  };
  const moveLargePreviewElement = (event) => {
    const interaction = largePreviewInteractionRef.current;
    if (!interaction.type || !largePreviewRef.current) return;
    const bounds = largePreviewRef.current.getBoundingClientRect();
    if (interaction.type === 'headline-move' || interaction.type === 'cta-move' || interaction.type === 'logo-move' || interaction.type === 'overlay-move' || interaction.type === 'image-focus') {
      const x = Number(clamp(((event.clientX - bounds.left) / bounds.width) * 100, 0, 100).toFixed(2));
      const y = Number(clamp(((event.clientY - bounds.top) / bounds.height) * 100, 0, 100).toFixed(2));
      if (interaction.type === 'headline-move') updateActiveCopyValues({ visual_headline_x_percent: clamp(x, 3, 75), visual_headline_y_percent: clamp(y, 5, 88) });
      if (interaction.type === 'cta-move') updateActiveCopyValues({ visual_cta_x_percent: clamp(x, 1, 95), visual_cta_y_percent: clamp(y, 2, 96) });
      if (interaction.type === 'logo-move') updateActiveCopyValues({ visual_partner_logo_x_percent: x, visual_partner_logo_y_percent: y });
      if (interaction.type === 'overlay-move') updateActiveCopyValues({ image_overlay_x_percent: x, image_overlay_y_percent: y });
      if (interaction.type === 'image-focus') updateActiveCopyValues({ visual_image_x: x, visual_image_y: y });
      return;
    }
    if (interaction.type === 'headline-resize') {
      const delta = ((event.clientX - interaction.startClientX) / bounds.width) * 180;
      const minimum = SMALLER_HEADLINE_IDS.has(activeAssetId) ? 35 : 20;
      const maximum = SMALLER_HEADLINE_IDS.has(activeAssetId) ? 120 : 160;
      updateActiveCopy('visual_headline_size_percent', Number(clamp(interaction.startHeadlineSize + delta, minimum, maximum).toFixed(2)));
      return;
    }
    if (interaction.type === 'cta-resize') {
      const delta = ((event.clientX - interaction.startClientX) / bounds.width) * 180;
      updateActiveCopy('visual_cta_size_percent', Number(clamp(interaction.startCtaSize + delta, 50, 200).toFixed(2)));
      return;
    }
    if (interaction.type === 'logo-resize') {
      const centreX = bounds.left + ((activePartnerLogoX / 100) * bounds.width);
      const centreY = bounds.top + ((activePartnerLogoY / 100) * bounds.height);
      const diameter = Math.hypot(event.clientX - centreX, event.clientY - centreY) * 2;
      updateActiveCopy('visual_logo_size_percent', Number(clamp(
        (diameter / bounds.width) * 100,
        minimumLogoSizeFor(activeAssetId),
        20
      ).toFixed(2)));
      return;
    }
    if (interaction.type === 'overlay-resize') {
      const delta = ((event.clientX - interaction.startClientX) / bounds.width) * 160;
      updateActiveCopy('image_overlay_size_percent', Number(clamp(interaction.startOverlaySize + delta, 5, 120).toFixed(2)));
    }
  };
  const handleFormatOverlayUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      onError?.('Choose a PNG, WebP, or JPG image overlay.');
      return;
    }
    setIsUploadingFormatOverlay(true);
    setFormatOverlayUploadProgress(0);
    try {
      const asset = await uploadMediaPackageStoryAsset({
        workspaceId,
        storyId: `format_${activeAssetId}`,
        file,
        assetSlot: 'image_overlay',
        onProgress: setFormatOverlayUploadProgress,
      });
      updateActiveCopyValues({
        image_overlay_url: asset.url,
        image_overlay_storage_path: asset.storagePath,
        image_overlay_content_type: asset.contentType,
        image_overlay_fingerprint: asset.fingerprint,
        image_overlay_filename: file.name,
        image_overlay_disabled: false,
        image_overlay_size_percent: activeCopy.image_overlay_size_percent ?? 35,
        image_overlay_x_percent: activeCopy.image_overlay_x_percent ?? 50,
        image_overlay_y_percent: activeCopy.image_overlay_y_percent ?? 38,
      });
    } catch (error) {
      onError?.(error.message || 'The extra image overlay could not be uploaded.');
    } finally {
      setIsUploadingFormatOverlay(false);
    }
  };
  const resetActiveHeadlineLayout = () => {
    const currentAssetCopy = { ...activeCopyOverrides };
    delete currentAssetCopy.visual_headline_size_percent;
    delete currentAssetCopy.visual_headline_x_percent;
    delete currentAssetCopy.visual_headline_y_percent;
    delete currentAssetCopy.visual_cta_size_percent;
    delete currentAssetCopy.visual_cta_x_percent;
    delete currentAssetCopy.visual_cta_y_percent;
    delete currentAssetCopy.visual_headline_color;
    delete currentAssetCopy.visual_cta_color;
    delete currentAssetCopy.visual_logo_size_percent;
    delete currentAssetCopy.visual_partner_logo_x_percent;
    delete currentAssetCopy.visual_partner_logo_y_percent;
    delete currentAssetCopy.visual_image_x;
    delete currentAssetCopy.visual_image_y;
    delete currentAssetCopy.visual_overlay_strength;
    delete currentAssetCopy.image_overlay_size_percent;
    delete currentAssetCopy.image_overlay_x_percent;
    delete currentAssetCopy.image_overlay_y_percent;
    delete currentAssetCopy.image_overlay_opacity_percent;
    delete currentAssetCopy.image_overlay_disabled;
    onConfigChange('asset_copy', {
      ...(config.asset_copy || {}),
      [activeCopyId]: currentAssetCopy,
    });
  };
  const storyCopy = { ...config, ...(config.asset_copy?.instagram_story || {}) };
  const storyHasManualY = storyCopy.visual_headline_y_percent !== null
    && storyCopy.visual_headline_y_percent !== undefined
    && storyCopy.visual_headline_y_percent !== '';
  const storyHeadlineStyle = {
    fontFamily: "'Playfair Display', Georgia, serif",
    fontSize: `${clamp(Number(storyCopy.visual_headline_size_percent) || 100, 50, 160) / 100}rem`,
    left: `${clamp(Number(storyCopy.visual_headline_x_percent) || 7.5, 3, 75)}%`,
    right: '5%',
    color: storyCopy.visual_headline_color || '#ffffff',
    ...(storyHasManualY
      ? { top: `${clamp(Number(storyCopy.visual_headline_y_percent) || 0, 5, 88)}%` }
      : { top: `${DEFAULT_HEADLINE_Y.instagram_story}%` }),
  };
  const storyCtaStyle = {
    fontSize: `${clamp(Number(storyCopy.visual_cta_size_percent) || 100, 50, 200) / 100}rem`,
    left: `${clamp(numberOrDefault(storyCopy.visual_cta_x_percent, DEFAULT_CTA_POSITION.instagram_story.x), 1, 95)}%`,
    top: `${clamp(numberOrDefault(storyCopy.visual_cta_y_percent, DEFAULT_CTA_POSITION.instagram_story.y), 2, 96)}%`,
    right: '4%',
    color: storyCopy.visual_cta_color || storyCopy.brand_accent_color || '#57bfdc',
  };
  const renderHeadlinePreviewHandle = () => (
    <div
      role="button"
      tabIndex={0}
      aria-label={`Move ${activeSpec.label} headline`}
      title="Drag to position the headline. Pull the round handle to resize it."
      onPointerDown={(event) => startLargePreviewInteraction(event, 'headline-move')}
      className={`absolute z-[60] touch-none border-2 border-dashed border-fuchsia-300 bg-fuchsia-950/10 ${largePreviewInteraction === 'headline-move' ? 'cursor-grabbing' : 'cursor-move'}`}
      style={{
        left: `${activeHeadlineX}%`,
        top: `${activeHeadlineY}%`,
        width: `${Math.max(12, 96 - Number(activeHeadlineX))}%`,
        height: `${clamp(activeHeadlineSize * 0.12, 6, 24)}%`,
      }}
    >
      <span className="absolute -top-6 left-0 whitespace-nowrap rounded bg-fuchsia-700 px-2 py-1 text-[10px] font-semibold text-white">Headline · {Math.round(activeHeadlineSize)}%</span>
      <button type="button" aria-label={`Resize ${activeSpec.label} headline`} title="Drag to resize the headline" onPointerDown={(event) => startLargePreviewInteraction(event, 'headline-resize')} className="absolute -bottom-2 -right-2 h-5 w-5 cursor-nwse-resize rounded-full border-2 border-white bg-fuchsia-600 shadow-md" />
    </div>
  );
  const renderCtaPreviewHandle = () => activeAssetId === 'onsite_qr' ? null : (
    <div
      role="button"
      tabIndex={0}
      aria-label={`Move ${activeSpec.label} call to action`}
      title="Drag to position the call to action. Pull the round handle to resize it."
      onPointerDown={(event) => startLargePreviewInteraction(event, 'cta-move')}
      className={`absolute z-[68] touch-none border-2 border-dashed border-emerald-300 bg-emerald-950/10 ${largePreviewInteraction === 'cta-move' ? 'cursor-grabbing' : 'cursor-move'}`}
      style={{
        left: `${activeCtaX}%`,
        top: `${activeCtaY}%`,
        width: `${clamp(96 - activeCtaX, 8, 75)}%`,
        height: `${activeCtaBoxHeight}%`,
      }}
    >
      <span className="absolute -top-6 left-0 whitespace-nowrap rounded bg-emerald-700 px-2 py-1 text-[10px] font-semibold text-white">Call to action · {Math.round(activeCtaSize)}%</span>
      <button type="button" aria-label={`Resize ${activeSpec.label} call to action`} title="Drag to resize the call to action" onPointerDown={(event) => startLargePreviewInteraction(event, 'cta-resize')} className="absolute -bottom-2 -right-2 h-5 w-5 cursor-nwse-resize rounded-full border-2 border-white bg-emerald-500 shadow-md" />
    </div>
  );

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2"><Image className="h-5 w-5 text-sky-600" /><h3 className="text-lg font-semibold text-slate-900">Visual editor</h3></div>
          <p className="mt-1 max-w-3xl text-sm text-slate-500">Choose a format below. Drag elements to position them and use the text tools to edit. Previews update automatically.</p>
        </div>
        <button type="button" onClick={generateAll} disabled={isRendering} className="rounded-lg bg-sky-600 px-4 py-2 text-sm font-semibold text-white hover:bg-sky-700 disabled:bg-slate-400">
          {isRendering ? 'Updating previews...' : 'Update all previews'}
        </button>
      </div>

      <nav className="mt-5 flex gap-2 overflow-x-auto pb-2" aria-label="Media formats">
        {[
          ...MEDIA_ASSET_SPECS.map((spec) => ({ ...spec, label: spec.id === 'instagram_story' ? 'Story image' : spec.label })),
          ...(storyEditor ? [{ id: 'story_series', label: 'Story videos', width: 1080, height: 1920 }] : []),
        ].map((spec) => (
          <button key={spec.id} type="button" aria-pressed={activeAssetId === spec.id} onClick={() => { setActiveAssetId(spec.id); setSelectedTextLayer(0); }} className={`shrink-0 rounded-xl border px-4 py-3 text-left ${activeAssetId === spec.id ? 'border-sky-600 bg-sky-50 text-sky-900 ring-1 ring-sky-600' : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'}`}>
            <span className="block text-sm font-semibold">{spec.label}</span>
            <span className="mt-1 block text-xs opacity-70">{spec.width} × {spec.height}</span>
          </button>
        ))}
      </nav>
      <div hidden={activeAssetId !== 'story_series'} className="mt-4">{storyEditor}</div>
      <div className={activeAssetId === 'story_series' ? 'hidden' : 'flex flex-col'}>
      {['instagram_feed', 'instagram_story', 'social_link'].includes(activeAssetId) && (
        <details key={activeCopyId} className="order-2 mt-4 rounded-xl border border-slate-200 bg-white p-4">
          <summary className="cursor-pointer text-sm font-semibold text-slate-700">Image for {activeSpec.label} · {activeCopyOverrides.format_image_url ? 'Custom image' : 'Using campaign image'}</summary>
          <div className="mt-4">
            <ImageUploader
              imageUrl={activeCopyOverrides.format_image_url || imageSource || ''}
              onImageUploaded={(url, attribution = null) => onConfigChange('asset_copy', (current = {}) => ({
                ...current,
                [activeCopyId]: { ...activeCopyOverrides, ...(current[activeCopyId] || {}), format_image_url: url || '', format_image_attribution: attribution },
              }))}
              itineraryMetadata={{ ...packageData.guide, name: packageData.guide.guide_title || config.guide_title, language: config.output_language || packageData.guide.language }}
              poiName={config.venue || config.guide_title}
              attributionData={activeCopyOverrides.format_image_url ? activeCopyOverrides.format_image_attribution : config.campaign_image_attribution}
              label="Image for this format only"
              previewSize="h-40"
              maxWidth={2400}
              maxSizeKB={1200}
            />
            <p className="mt-2 text-sm text-slate-500">Choose a different image here, or use the shared Approved campaign image. Other formats keep their own selection.</p>
            {activeCopyOverrides.format_image_url && <button type="button" onClick={() => updateActiveCopyValues({ format_image_url: '', format_image_attribution: null })} className="mt-3 rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700">Use campaign image</button>}
          </div>
        </details>
      )}
      <details className="group order-2 mt-5 overflow-hidden rounded-xl border border-slate-200 bg-slate-50">
        <summary className="flex cursor-pointer list-none items-center justify-between gap-4 px-4 py-3 text-sm font-semibold text-slate-800 marker:hidden [&::-webkit-details-marker]:hidden">
          <span>Images, branding & advanced settings</span>
          <span className="text-xs font-medium text-slate-500 group-open:hidden">Open settings</span>
          <span className="hidden text-xs font-medium text-slate-500 group-open:inline">Close settings</span>
        </summary>
        <div className="border-t border-slate-200 p-4">
      {assetUploadFeedback && (
        <div className={`mb-4 flex items-center gap-2 rounded-lg border px-3 py-2.5 text-sm font-medium ${assetUploadFeedback.tone === 'error'
          ? 'border-rose-200 bg-rose-50 text-rose-800'
          : assetUploadFeedback.tone === 'success'
            ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
            : 'border-sky-200 bg-sky-50 text-sky-800'}`}
        >
          {assetUploadFeedback.tone === 'working'
            ? <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
            : assetUploadFeedback.tone === 'success'
              ? <Check className="h-4 w-4 shrink-0" />
              : <AlertCircle className="h-4 w-4 shrink-0" />}
          <span>{assetUploadFeedback.text}</span>
        </div>
      )}
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 normal-case md:col-span-2 xl:col-span-4">
          <ImageUploader
            imageUrl={config.campaign_image_url || ''}
            onImageUploaded={handleCampaignImageSelected}
            itineraryMetadata={{
              ...packageData.guide,
              name: packageData.guide.guide_title || config.guide_title,
              language: config.output_language || packageData.guide.language,
              filename: packageData.guide.guide_id ? `${packageData.guide.guide_id}.json` : undefined,
            }}
            poiName={config.venue || packageData.guide.venue || config.guide_title}
            attributionData={config.campaign_image_attribution || null}
            label="Approved campaign image"
            previewSize="h-64"
            maxWidth={2400}
            maxSizeKB={1200}
          />
          <p className="mt-2 text-xs text-slate-500">Upload an image, paste its URL, or search Wikimedia Commons. A Wikimedia selection is uploaded to the guide storage and keeps its licence and attribution data.</p>
        </div>
        <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Partner business logo URL<input className={`${controlClass} mt-1`} type="url" value={config.partner_logo_url || ''} onChange={(event) => onConfigChange('partner_logo_url', event.target.value)} placeholder="https://..." /></label>
        <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Or upload partner logo<span className="mt-1 flex h-[38px] cursor-pointer items-center justify-center gap-2 rounded-lg border border-dashed border-slate-300 bg-slate-50 text-sm font-medium normal-case text-slate-600"><Upload className="h-4 w-4" />Choose logo<input className="hidden" type="file" accept="image/*" onChange={handlePartnerLogoUpload} /></span></label>
        <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Primary colour<input className={`${controlClass} mt-1 h-[38px] p-1`} type="color" value={config.brand_primary_color || '#3b87a9'} onChange={(event) => onConfigChange('brand_primary_color', event.target.value)} /></label>
        <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Secondary colour<input className={`${controlClass} mt-1 h-[38px] p-1`} type="color" value={config.brand_accent_color || '#57bfdc'} onChange={(event) => onConfigChange('brand_accent_color', event.target.value)} /></label>
        <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Gradient start<input className={`${controlClass} mt-1 h-[38px] p-1`} type="color" value={config.visual_gradient_start_color || '#020617'} onChange={(event) => onConfigChange('visual_gradient_start_color', event.target.value)} /></label>
        <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Gradient end<input className={`${controlClass} mt-1 h-[38px] p-1`} type="color" value={config.visual_gradient_end_color || '#020617'} onChange={(event) => onConfigChange('visual_gradient_end_color', event.target.value)} /></label>
        {activeAssetId === 'social_link' && (
          <label className="text-xs font-semibold uppercase tracking-wide text-slate-500 md:col-span-2">
            Landscape variation
            <select className={`${controlClass} mt-1`} value={selectedLandscapeVariation} disabled={isRendering} onChange={(event) => onConfigChange('landscape_variation', event.target.value)}>
              {LANDSCAPE_VARIATIONS.map((variation) => <option key={variation.id} value={variation.id}>{variation.label}</option>)}
            </select>
            <span className="mt-1 block text-xs font-normal normal-case text-slate-500">Each variation keeps its own text and layout. The selected variation is included in the package at 1920 × 1080.</span>
          </label>
        )}
        {activeAssetId === 'phone_mockup' ? (
          <div className="grid gap-4 rounded-xl border border-sky-200 bg-sky-50 p-4 md:col-span-2 xl:col-span-4 md:grid-cols-2 xl:grid-cols-4">
            <div className="md:col-span-2 xl:col-span-4">
              <div className="font-semibold normal-case text-slate-900">Layered phone mockup</div>
              <p className="mt-1 text-xs font-normal normal-case leading-5 text-slate-600">Upload the transparent phone PNG as the top layer and the client screenshot as the layer beneath it. The screen viewport prevents the screenshot from leaking outside the phone.</p>
            </div>
            <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Phone frame PNG URL<input className={`${controlClass} mt-1`} type="url" value={config.phone_frame_url || ''} onChange={(event) => onConfigChange('phone_frame_url', event.target.value)} placeholder="https://.../phone-frame.png" /></label>
            <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Or upload phone PNG<span className="mt-1 flex h-[38px] cursor-pointer items-center justify-center gap-2 rounded-lg border border-dashed border-slate-300 bg-white text-sm font-medium normal-case text-slate-600"><Upload className="h-4 w-4" />Choose PNG<input className="hidden" type="file" accept="image/png,image/webp" onChange={(event) => handleLayerUpload(event, onLocalPhoneFrameChange, 'Phone frame')} /></span></label>
            <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Client screen image URL<input className={`${controlClass} mt-1`} type="url" value={config.phone_screen_url || ''} onChange={(event) => onConfigChange('phone_screen_url', event.target.value)} placeholder="https://.../client-screen.jpg" /></label>
            <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Or upload client image<span className="mt-1 flex h-[38px] cursor-pointer items-center justify-center gap-2 rounded-lg border border-dashed border-slate-300 bg-white text-sm font-medium normal-case text-slate-600"><Upload className="h-4 w-4" />Choose image<input className="hidden" type="file" accept="image/*" onChange={(event) => handleLayerUpload(event, onLocalPhoneScreenChange, 'Client screen image')} /></span></label>
            <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Gradient start<input className={`${controlClass} mt-1 h-[38px] p-1`} type="color" value={config.phone_background_start_color || '#020617'} onChange={(event) => onConfigChange('phone_background_start_color', event.target.value)} /></label>
            <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Gradient end<input className={`${controlClass} mt-1 h-[38px] p-1`} type="color" value={config.phone_background_end_color || '#374151'} onChange={(event) => onConfigChange('phone_background_end_color', event.target.value)} /></label>
            <label className="text-xs font-semibold uppercase tracking-wide text-slate-500 md:col-span-2">High-resolution partner logo for exports<span className="mt-1 flex h-[42px] cursor-pointer items-center justify-center gap-2 rounded-lg border border-dashed border-sky-400 bg-white text-sm font-semibold normal-case text-sky-800"><Upload className="h-4 w-4" />Upload high-resolution logo<input className="hidden" type="file" accept="image/png,image/webp,image/svg+xml,image/jpeg" onChange={handlePartnerLogoUpload} /></span><span className="mt-1 block font-normal normal-case text-slate-500">Prefer a transparent PNG, WebP, or SVG. This replaces the smaller website logo in all generated Media Package assets.</span></label>
            <label className="flex items-center gap-3 rounded-lg border border-sky-200 bg-white px-3 py-3 text-sm font-semibold normal-case text-slate-700 md:col-span-2"><input type="checkbox" checked={Boolean(config.phone_show_qr)} onChange={(event) => onConfigChange('phone_show_qr', event.target.checked)} className="h-4 w-4 accent-sky-600" /><span>Include the Whole Guide QR code on this poster</span></label>
          </div>
        ) : activeAssetId === 'onsite_qr' ? (
          <div className="rounded-xl border border-cyan-200 bg-cyan-50 p-4 md:col-span-2 xl:col-span-4">
            <div className="font-semibold normal-case text-slate-900">Fixed A5 desk-display design</div>
            <p className="mt-1 text-xs font-normal normal-case leading-5 text-slate-600">This format automatically uses desk-display-QR.jpg as its full background. The Whole Guide QR, large partner logo, and Roboto headline are layered onto the prepared design.</p>
            <p className="mt-2 text-xs font-normal normal-case text-slate-500">Edit the headline with the standard tools attached to the large preview.</p>
          </div>
        ) : (
          <>
            <div className="hidden md:block md:col-span-2" />
            <label className="text-xs font-semibold uppercase tracking-wide text-slate-500 md:col-span-2">@mentions<input className={`${controlClass} mt-1`} value={config.social_mentions || ''} onChange={(event) => updateSocialMentions(event.target.value)} placeholder="@guide.sofia @partner" /><span className="mt-1 block text-xs font-normal normal-case text-slate-400">@guide.sofia and the required partner handle are kept automatically. Add only verified extra accounts.</span></label>
            <label className="text-xs font-semibold uppercase tracking-wide text-slate-500 md:col-span-2">Image alt text<input className={`${controlClass} mt-1`} value={activeCopy.image_alt_text || ''} onChange={(event) => updateActiveCopy('image_alt_text', event.target.value)} placeholder="Describe the approved photograph for screen-reader users" /></label>
          </>
        )}
      </div>
      <p className="mt-3 text-xs text-slate-500">If a remote image cannot be embedded because of its host permissions, the preview uses the branded background. Upload the approved photograph locally to render it reliably.</p>

      {activeAssetId !== 'onsite_qr' && activeAssetId !== 'phone_mockup' && activeAssetId !== 'instagram_feed' && <details className="mt-5 rounded-xl border border-slate-200 bg-slate-50 p-4">
        <summary className="cursor-pointer text-xs font-semibold uppercase tracking-wide text-slate-600">Layout editor</summary>
        <div className="mt-3 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          <label className="text-xs font-medium text-slate-600">Horizontal image focus: {config.visual_image_x ?? 50}%<input className="mt-2 w-full accent-sky-600" type="range" min="0" max="100" value={config.visual_image_x ?? 50} onChange={(event) => onConfigChange('visual_image_x', Number(event.target.value))} /></label>
          <label className="text-xs font-medium text-slate-600">Vertical image focus: {config.visual_image_y ?? 50}%<input className="mt-2 w-full accent-sky-600" type="range" min="0" max="100" value={config.visual_image_y ?? 50} onChange={(event) => onConfigChange('visual_image_y', Number(event.target.value))} /></label>
          <label className="text-xs font-medium text-slate-600">Overlay strength: {config.visual_overlay_strength ?? 70}%<input className="mt-2 w-full accent-sky-600" type="range" min="20" max="95" value={config.visual_overlay_strength ?? 70} onChange={(event) => onConfigChange('visual_overlay_strength', Number(event.target.value))} /></label>
          <label className="text-xs font-medium text-slate-600">Text position<select className={`${controlClass} mt-1`} value={config.visual_text_position || 'bottom'} onChange={(event) => onConfigChange('visual_text_position', event.target.value)}><option value="top">Top</option><option value="center">Centre</option><option value="bottom">Bottom</option></select></label>
        </div>
      </details>}

      {activeSupportsExtraOverlay && activeAssetId !== 'instagram_feed' && <details className="mt-4 rounded-xl border border-fuchsia-200 bg-fuchsia-50 p-4">
        <summary className="cursor-pointer text-xs font-semibold uppercase tracking-wide text-fuchsia-700">GuideSofia branding</summary>
        <div className="mt-3 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          <div className="rounded-lg border border-fuchsia-200 bg-white p-3 text-xs leading-5 text-slate-600 md:col-span-2">
            <div className="font-semibold text-slate-800">{activeOverlayEnabled ? activeCopy.image_overlay_filename || 'Default GuideSofia overlay' : 'Overlay hidden'}</div>
            <p className="mt-1">This layer is saved independently for {activeSpec.label}.</p>
          </div>
          <label className="flex cursor-pointer items-center justify-center gap-2 rounded-lg border border-dashed border-fuchsia-400 bg-white px-3 py-3 text-xs font-semibold text-fuchsia-800"><Upload className="h-4 w-4" />{isUploadingFormatOverlay ? `Uploading ${formatOverlayUploadProgress}%` : 'Upload overlay'}<input className="hidden" type="file" accept="image/png,image/webp,image/jpeg" disabled={isUploadingFormatOverlay} onChange={handleFormatOverlayUpload} /></label>
          {activeOverlayEnabled
            ? <button type="button" onClick={() => updateActiveCopy('image_overlay_disabled', true)} className="rounded-lg border border-slate-300 bg-white px-3 py-3 text-xs font-semibold text-slate-700">Hide overlay</button>
            : <button type="button" onClick={() => updateActiveCopyValues({ image_overlay_disabled: false, image_overlay_url: '', image_overlay_storage_path: '', image_overlay_filename: '' })} className="rounded-lg border border-fuchsia-300 bg-white px-3 py-3 text-xs font-semibold text-fuchsia-800">Restore default overlay</button>}
          {activeOverlayEnabled && <>
            <label className="text-xs font-medium text-slate-600">Overlay size: {Math.round(activeOverlaySize)}%<input className="mt-2 w-full accent-fuchsia-600" type="range" min="5" max="120" value={activeOverlaySize} onChange={(event) => updateActiveCopy('image_overlay_size_percent', Number(event.target.value))} /></label>
            <label className="text-xs font-medium text-slate-600">Horizontal: {Math.round(activeOverlayX)}%<input className="mt-2 w-full accent-fuchsia-600" type="range" min="0" max="100" value={activeOverlayX} onChange={(event) => updateActiveCopy('image_overlay_x_percent', Number(event.target.value))} /></label>
            <label className="text-xs font-medium text-slate-600">Vertical: {Math.round(activeOverlayY)}%<input className="mt-2 w-full accent-fuchsia-600" type="range" min="0" max="100" value={activeOverlayY} onChange={(event) => updateActiveCopy('image_overlay_y_percent', Number(event.target.value))} /></label>
          </>}
        </div>
      </details>}

      <details className="mt-4 rounded-xl border border-sky-200 bg-sky-50 p-4">
        <summary className="cursor-pointer text-xs font-semibold uppercase tracking-wide text-sky-700">Visual layout: {activeSpec.label}</summary>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="mt-1 text-xs text-slate-600">These settings apply only to the selected format.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={onSaveLayoutDefault} disabled={layoutPreferenceStatus === 'saving'} className="rounded-lg bg-sky-700 px-3 py-1.5 text-xs font-semibold text-white disabled:bg-slate-400">
              {layoutPreferenceStatus === 'saving' ? 'Saving default...' : layoutPreferenceStatus === 'saved' ? 'Default saved' : 'Save all layouts as my default'}
            </button>
            {hasLayoutDefault && <button type="button" onClick={onApplyLayoutDefault} className="rounded-lg border border-sky-400 bg-white px-3 py-1.5 text-xs font-semibold text-sky-800">{layoutPreferenceStatus === 'applied' ? 'Default applied' : 'Apply my saved default'}</button>}
            <button type="button" onClick={resetActiveHeadlineLayout} className="rounded-lg border border-sky-300 bg-white px-3 py-1.5 text-xs font-semibold text-sky-800">Reset selected format</button>
          </div>
        </div>
        <div className="mt-3 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          {activeAssetId !== 'phone_mockup' && activeAssetId !== 'instagram_feed' && <label className="text-xs font-medium text-slate-700">Logo size: {activeLogoSize.toFixed(1)}%<input className="mt-2 w-full accent-sky-600" type="range" min={activeAssetId === 'onsite_qr' ? 26.4 : minimumLogoSizeFor(activeAssetId)} max={activeAssetId === 'onsite_qr' ? 33 : 20} step="0.5" value={activeLogoSize} onChange={(event) => updateActiveCopy('visual_logo_size_percent', Number(event.target.value))} /></label>}
          {activeAllowsFreePartnerLogo && activeAssetId !== 'instagram_feed' && <>
            <label className="text-xs font-medium text-slate-700">Partner logo horizontal: {Math.round(activePartnerLogoX)}%<input className="mt-2 w-full accent-sky-600" type="range" min="0" max="100" step="1" value={activePartnerLogoX} onChange={(event) => updateActiveCopy('visual_partner_logo_x_percent', Number(event.target.value))} /></label>
            <label className="text-xs font-medium text-slate-700">Partner logo vertical: {Math.round(activePartnerLogoY)}%<input className="mt-2 w-full accent-sky-600" type="range" min="0" max="100" step="1" value={activePartnerLogoY} onChange={(event) => updateActiveCopy('visual_partner_logo_y_percent', Number(event.target.value))} /></label>
            <div className="rounded-lg border border-cyan-200 bg-white p-3 text-xs leading-5 text-slate-600 md:col-span-2">Drag the partner logo directly on the large preview, or use the two position controls.</div>
          </>}
          {activeAssetId === 'phone_mockup' && <>
            <label className="text-xs font-medium text-slate-700">Phone size: {config.phone_size_percent ?? 72}%<input className="mt-2 w-full accent-sky-600" type="range" min="20" max="120" value={config.phone_size_percent ?? 72} onChange={(event) => onConfigChange('phone_size_percent', Number(event.target.value))} /></label>
            <label className="text-xs font-medium text-slate-700">Phone horizontal position: {config.phone_x_percent ?? 50}%<input className="mt-2 w-full accent-sky-600" type="range" min="0" max="100" value={config.phone_x_percent ?? 50} onChange={(event) => onConfigChange('phone_x_percent', Number(event.target.value))} /></label>
            <label className="text-xs font-medium text-slate-700">Phone vertical position: {config.phone_y_percent ?? 52}%<input className="mt-2 w-full accent-sky-600" type="range" min="0" max="100" value={config.phone_y_percent ?? 52} onChange={(event) => onConfigChange('phone_y_percent', Number(event.target.value))} /></label>
            <label className="text-xs font-medium text-slate-700">Phone tilt: {config.phone_tilt_degrees ?? -8}°<input className="mt-2 w-full accent-sky-600" type="range" min="-45" max="45" value={config.phone_tilt_degrees ?? -8} onChange={(event) => onConfigChange('phone_tilt_degrees', Number(event.target.value))} /></label>
            <label className="text-xs font-medium text-slate-700">Screen corner radius: {config.phone_screen_corner_radius_percent ?? 5}%<input className="mt-2 w-full accent-sky-600" type="range" min="0" max="25" step="0.5" value={config.phone_screen_corner_radius_percent ?? 5} onChange={(event) => onConfigChange('phone_screen_corner_radius_percent', Number(event.target.value))} /></label>
            <label className="text-xs font-medium text-slate-700">Logo size: {config.phone_overlay_logo_size_percent ?? 8}%<input className="mt-2 w-full accent-sky-600" type="range" min="3" max="40" step="0.5" value={config.phone_overlay_logo_size_percent ?? 8} onChange={(event) => onConfigChange('phone_overlay_logo_size_percent', Number(event.target.value))} /></label>
            <label className="text-xs font-medium text-slate-700">Partner logo inner padding, all formats: {config.phone_partner_logo_padding_percent ?? 12}%<input className="mt-2 w-full accent-sky-600" type="range" min="0" max="35" step="1" value={config.phone_partner_logo_padding_percent ?? 12} onChange={(event) => onConfigChange('phone_partner_logo_padding_percent', Number(event.target.value))} /></label>
            {config.phone_show_qr && <label className="text-xs font-medium text-slate-700">QR size: {config.phone_qr_size_percent ?? 18}%<input className="mt-2 w-full accent-sky-600" type="range" min="6" max="40" step="1" value={config.phone_qr_size_percent ?? 18} onChange={(event) => onConfigChange('phone_qr_size_percent', Number(event.target.value))} /></label>}
            <div className="rounded-lg border border-cyan-200 bg-white p-3 text-xs leading-5 text-slate-600 md:col-span-2">In edit mode, drag the four cyan screen corners and the purple Partner, GuideSofia, and Text handles directly on the large preview.</div>
            <button type="button" onClick={() => onConfigChange('phone_screen_corners', DEFAULT_PHONE_CORNERS)} className="rounded-lg border border-sky-300 bg-white px-3 py-2 text-xs font-semibold text-sky-800">Reset screen corners</button>
            <button
              type="button"
              onClick={() => {
                setPhonePositionsEditing((current) => !current);
                setPhonePositionsSaved(phonePositionsEditing);
              }}
              className={`rounded-lg px-3 py-2 text-xs font-semibold text-white ${phonePositionsEditing ? 'bg-emerald-700' : 'bg-sky-700'}`}
            >
              {phonePositionsEditing ? 'Save all positions' : phonePositionsSaved ? 'Positions saved · edit again' : 'Edit positions'}
            </button>
          </>}
        </div>
      </details>
        </div>
      </details>

      <div className="order-1 mt-4 overflow-hidden rounded-2xl border border-slate-300 bg-slate-950 shadow-lg">
        <div className="flex items-center justify-between gap-4 border-b border-white/10 px-4 py-3 text-white">
          <div>
            <div className="text-sm font-semibold">{activeSpec.label}</div>
            <div className="mt-0.5 text-xs text-slate-400">{activeSpec.width} × {activeSpec.height} · preview updates automatically</div>
          </div>
          <button type="button" onClick={updateActivePreview} disabled={isRendering} className="shrink-0 rounded-lg bg-cyan-500 px-3 py-2 text-xs font-semibold text-slate-950 hover:bg-cyan-400 disabled:bg-slate-600 disabled:text-slate-300">
            {renderingAssetIds.includes(activeSpec.id) ? 'Updating…' : 'Update preview'}
          </button>
        </div>
        <div className="border-b border-white/10 bg-slate-900 p-3 text-white">
          <div className="mb-3 flex gap-2" aria-label="Text layers">
            {(activeAssetId === 'phone_mockup' ? ['Title', 'Subheader'] : activeAssetId === 'onsite_qr' ? ['Headline'] : ['Headline', 'Call to action']).map((label, index) => (
              <button type="button" key={label} aria-pressed={selectedTextLayer === index} onClick={() => setSelectedTextLayer(index)} className={`rounded-lg px-3 py-2 text-sm font-medium ${selectedTextLayer === index ? 'bg-white text-slate-900' : 'bg-white/10 text-slate-300'}`}>{label}</button>
            ))}
          </div>
          {activeAssetId === 'phone_mockup' ? (
            <div className="grid gap-2 xl:grid-cols-2">
              {[
                { label: 'Title', textKey: 'phone_overlay_text', sizeKey: 'phone_overlay_text_size_percent', colorKey: 'phone_overlay_text_color', positionKey: 'phone_overlay_text_position', size: config.phone_overlay_text_size_percent ?? 4.5, color: config.phone_overlay_text_color || '#ffffff', position: config.phone_overlay_text_position || { x: 7, y: 7 }, min: 1.5, max: 12 },
                { label: 'Subheader', textKey: 'phone_subheader_text', sizeKey: 'phone_subheader_text_size_percent', colorKey: 'phone_subheader_text_color', positionKey: 'phone_subheader_position', size: config.phone_subheader_text_size_percent ?? 2.5, color: config.phone_subheader_text_color || '#d1d5db', position: config.phone_subheader_position || { x: 7, y: 16 }, min: 1, max: 10 },
              ].filter((tool, index) => index === selectedTextLayer).map((tool) => (
                <div key={tool.textKey} className="grid grid-cols-[auto_1fr_auto] items-center gap-2 rounded-lg border border-white/10 bg-slate-950/70 p-3 text-sm">
                  <span className="font-semibold">{tool.label}</span>
                  <input aria-label={`${tool.label} text`} className="min-w-0 rounded border border-white/15 bg-white/10 px-2 py-1 text-white" value={config[tool.textKey] || ''} onChange={(event) => onConfigChange(tool.textKey, event.target.value)} />
                  <input aria-label={`${tool.label} colour`} className="h-7 w-8 cursor-pointer rounded border border-white/20 bg-transparent p-0.5" type="color" value={tool.color} onChange={(event) => onConfigChange(tool.colorKey, event.target.value)} />
                  <span>Size {Number(tool.size).toFixed(1)}</span>
                  <input aria-label={`${tool.label} size`} className="accent-sky-500" type="range" min={tool.min} max={tool.max} step="0.25" value={tool.size} onChange={(event) => onConfigChange(tool.sizeKey, Number(event.target.value))} />
                  <details className="col-span-3 text-xs text-slate-300"><summary className="cursor-pointer py-2">Precise position</summary><span className="whitespace-nowrap">X <input aria-label={`${tool.label} horizontal position`} className="w-10 rounded bg-white/10 px-1 py-0.5" type="number" min="0" max="100" value={tool.position.x} onChange={(event) => onConfigChange(tool.positionKey, { ...tool.position, x: Number(event.target.value) })} /> Y <input aria-label={`${tool.label} vertical position`} className="w-10 rounded bg-white/10 px-1 py-0.5" type="number" min="0" max="100" value={tool.position.y} onChange={(event) => onConfigChange(tool.positionKey, { ...tool.position, y: Number(event.target.value) })} /></span></details>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid gap-2 xl:grid-cols-2">
              {[
                { label: 'Headline', textKey: 'visual_headline', sizeKey: 'visual_headline_size_percent', colorKey: 'visual_headline_color', xKey: 'visual_headline_x_percent', yKey: 'visual_headline_y_percent', text: activeCopy.visual_headline || '', size: activeHeadlineSize, color: activeHeadlineColor, x: activeHeadlineX, y: activeHeadlineY, min: activeHeadlineMinimum, max: activeHeadlineMaximum },
                ...(activeAssetId === 'onsite_qr' ? [] : [{ label: 'Call to action', textKey: 'visual_cta', sizeKey: 'visual_cta_size_percent', colorKey: 'visual_cta_color', xKey: 'visual_cta_x_percent', yKey: 'visual_cta_y_percent', text: activeCopy.visual_cta || '', size: activeCtaSize, color: activeCtaColor, x: activeCtaX, y: activeCtaY, min: 50, max: 200 }]),
              ].filter((tool, index) => index === selectedTextLayer).map((tool) => (
                <div key={tool.textKey} className="grid grid-cols-[auto_1fr_auto] items-center gap-2 rounded-lg border border-white/10 bg-slate-950/70 p-3 text-sm">
                  <span className="font-semibold">{tool.label}</span>
                  <input aria-label={`${tool.label} text`} className="min-w-0 rounded border border-white/15 bg-white/10 px-2 py-1 text-white" value={tool.text} onChange={(event) => updateActiveCopy(tool.textKey, event.target.value)} />
                  <input aria-label={`${tool.label} colour`} className="h-7 w-8 cursor-pointer rounded border border-white/20 bg-transparent p-0.5" type="color" value={tool.color} onChange={(event) => updateActiveCopy(tool.colorKey, event.target.value)} />
                  <span>Size {Math.round(tool.size)}%</span>
                  <input aria-label={`${tool.label} size`} className="accent-sky-500" type="range" min={tool.min} max={tool.max} step="5" value={tool.size} onChange={(event) => updateActiveCopy(tool.sizeKey, Number(event.target.value))} />
                  <details className="col-span-3 text-xs text-slate-300"><summary className="cursor-pointer py-2">Precise position</summary><span className="whitespace-nowrap">X <input aria-label={`${tool.label} horizontal position`} className="w-10 rounded bg-white/10 px-1 py-0.5" type="number" min="0" max="100" value={Math.round(Number(tool.x))} onChange={(event) => updateActiveCopy(tool.xKey, Number(event.target.value))} /> Y <input aria-label={`${tool.label} vertical position`} className="w-10 rounded bg-white/10 px-1 py-0.5" type="number" min="0" max="100" value={Math.round(Number(tool.y))} onChange={(event) => updateActiveCopy(tool.yKey, Number(event.target.value))} /></span></details>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="flex min-h-[32rem] max-h-[78vh] items-center justify-center overflow-auto bg-[radial-gradient(circle_at_center,_#334155,_#020617)] p-5 md:p-8">
          {activeSpec.id === 'instagram_story' && storyVideoUrl ? (
            <div
              ref={largePreviewRef}
              className="relative h-[70vh] max-h-[760px] max-w-full aspect-[9/16] overflow-hidden bg-slate-950 shadow-2xl"
              onPointerMove={moveLargePreviewElement}
              onPointerUp={stopLargePreviewInteraction}
              onPointerCancel={stopLargePreviewInteraction}
            >
              <video src={storyVideoUrl} controls playsInline className="h-full w-full object-contain" />
              <div className="pointer-events-none absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-slate-950/95 via-slate-950/65 to-transparent" />
              <div className="pointer-events-none absolute z-10 font-bold leading-tight" style={storyHeadlineStyle}>{storyCopy.visual_headline || storyCopy.guide_title}</div>
              <div className="pointer-events-none absolute z-10 font-bold leading-tight" style={storyCtaStyle}>{storyCopy.visual_cta}</div>
              <SafeMarginOverlay assetId="instagram_story" />
              {renderHeadlinePreviewHandle()}
              {renderCtaPreviewHandle()}
            </div>
          ) : activeSpec.id === 'phone_mockup' && largePreviewUrl ? (
            <div className="relative h-[70vh] max-h-[760px] max-w-full aspect-[4/5] shadow-2xl">
              <img src={largePreviewUrl} alt={`${activeSpec.label} large preview`} className="h-full w-full object-contain" />
              <PhonePerspectiveOverlay
                config={config}
                frameSource={phoneFrameSource}
                onChange={(corners) => onConfigChange('phone_screen_corners', corners)}
                onPositionChange={(key, position) => onConfigChange(key, position)}
                isEditing={phonePositionsEditing}
              />
            </div>
          ) : largePreviewUrl ? (
            <div
              ref={largePreviewRef}
              className="relative max-h-[70vh] max-w-full shadow-2xl"
              style={{ width: `min(100%, ${(70 * activeSpec.width) / activeSpec.height}vh)`, aspectRatio: `${activeSpec.width} / ${activeSpec.height}` }}
              onPointerMove={moveLargePreviewElement}
              onPointerUp={stopLargePreviewInteraction}
              onPointerCancel={stopLargePreviewInteraction}
            >
              <img src={largePreviewUrl} alt={`${activeSpec.label} large preview`} className="h-full w-full object-contain" />
              <SafeMarginOverlay assetId={activeAssetId} />
              {renderHeadlinePreviewHandle()}
              {renderCtaPreviewHandle()}
              {activeAssetId === 'instagram_feed' && (
                <>
                <div className="absolute left-2 top-2 z-[70] flex max-w-[calc(100%-1rem)] flex-wrap items-center gap-1.5 rounded-lg border border-white/30 bg-slate-950/90 p-2 text-[10px] font-semibold text-white shadow-xl" onPointerDown={(event) => event.stopPropagation()}>
                  <span>Gradient {Math.round(activeOverlayStrength)}%</span>
                  <button type="button" aria-label="Reduce gradient strength" onClick={() => updateActiveCopy('visual_overlay_strength', clamp(activeOverlayStrength - 5, 20, 95))} className="h-6 w-6 rounded bg-white/15 text-sm">−</button>
                  <button type="button" aria-label="Increase gradient strength" onClick={() => updateActiveCopy('visual_overlay_strength', clamp(activeOverlayStrength + 5, 20, 95))} className="h-6 w-6 rounded bg-white/15 text-sm">+</button>
                  <span className="ml-1 border-l border-white/20 pl-2">GuideSofia branding</span>
                  <label className="cursor-pointer rounded bg-white/15 px-2 py-1.5">{isUploadingFormatOverlay ? `Uploading ${formatOverlayUploadProgress}%` : 'Upload'}<input className="hidden" type="file" accept="image/png,image/webp,image/jpeg" disabled={isUploadingFormatOverlay} onChange={handleFormatOverlayUpload} /></label>
                  <button type="button" onClick={() => updateActiveCopy('image_overlay_disabled', !activeOverlayEnabled)} className="rounded bg-white/15 px-2 py-1.5">{activeOverlayEnabled ? 'Hide' : 'Show'}</button>
                </div>
                <button
                  type="button"
                  aria-label="Move image focus"
                  title="Drag to change horizontal and vertical image focus"
                  onPointerDown={(event) => startLargePreviewInteraction(event, 'image-focus')}
                  className={`absolute z-[55] flex h-7 w-7 -translate-x-1/2 -translate-y-1/2 touch-none items-center justify-center rounded-full border-2 border-white bg-sky-600/90 text-xs font-bold text-white shadow-lg ring-2 ring-slate-950/50 ${largePreviewInteraction === 'image-focus' ? 'cursor-grabbing' : 'cursor-crosshair'}`}
                  style={{ left: `${activeImageX}%`, top: `${activeImageY}%` }}
                >＋</button>
                {activeOverlayEnabled && <div
                  role="button"
                  tabIndex={0}
                  aria-label="Move GuideSofia branding"
                  title="Drag to position. Pull the round handle to resize."
                  onPointerDown={(event) => startLargePreviewInteraction(event, 'overlay-move')}
                  className={`absolute z-[54] touch-none border-2 border-dashed border-amber-300 bg-amber-950/5 ${largePreviewInteraction === 'overlay-move' ? 'cursor-grabbing' : 'cursor-move'}`}
                  style={{
                    left: `${activeOverlayX}%`,
                    top: `${activeOverlayY}%`,
                    width: `${activeOverlaySize}%`,
                    height: `${clamp(activeOverlaySize * 0.62, 8, 74)}%`,
                    transform: 'translate(-50%, -50%)',
                  }}
                >
                  <span className="absolute -top-6 left-0 rounded bg-amber-600 px-2 py-1 text-[10px] font-semibold text-white">GuideSofia branding</span>
                  <button type="button" aria-label="Resize GuideSofia branding" title="Drag to resize branding" onPointerDown={(event) => startLargePreviewInteraction(event, 'overlay-resize')} className="absolute -bottom-2 -right-2 h-5 w-5 cursor-nwse-resize rounded-full border-2 border-white bg-amber-500 shadow-md" />
                </div>}
                </>
              )}
              {activeAllowsFreePartnerLogo && partnerLogoSource && (
                <div
                  role="button"
                  tabIndex={0}
                  aria-label="Move partner logo"
                  className="absolute z-[65] aspect-square -translate-x-1/2 -translate-y-1/2 cursor-move rounded-full border-2 border-dashed border-cyan-300 bg-transparent ring-2 ring-slate-950/30 touch-none"
                  style={{ left: `${activePartnerLogoX}%`, top: `${activePartnerLogoY}%`, width: `${activeLogoSize}%` }}
                  onPointerDown={(event) => startLargePreviewInteraction(event, 'logo-move')}
                >
                  {activeAssetId === 'instagram_feed' && <button
                    type="button"
                    aria-label="Resize partner logo"
                    title="Drag to resize partner logo"
                    onPointerDown={(event) => startLargePreviewInteraction(event, 'logo-resize')}
                    className="absolute -bottom-2 -right-2 h-5 w-5 cursor-nwse-resize rounded-full border-2 border-white bg-cyan-500 shadow-md"
                  />}
                </div>
              )}
            </div>
          ) : (
            <div className="text-sm text-slate-300">Preparing preview…</div>
          )}
        </div>
      </div>

      <details className="order-3 mt-5 rounded-xl border border-slate-200 p-4">
      <summary className="cursor-pointer text-sm font-semibold text-slate-700">All previews & downloads</summary>
      <div className="mt-4 grid gap-5 md:grid-cols-2 xl:grid-cols-3">
        {MEDIA_ASSET_SPECS.map((spec) => (
          <article key={spec.id} className="overflow-hidden rounded-xl border border-slate-200 bg-slate-50">
            <div className="relative flex aspect-[4/3] items-center justify-center overflow-hidden bg-slate-200 p-3">
              {spec.id === 'instagram_story' && storyVideoUrl ? (
                <div className="relative h-full max-w-full aspect-[9/16] overflow-hidden bg-slate-950 shadow-md">
                  <video src={storyVideoUrl} controls playsInline className="h-full w-full object-contain" />
                  <div className="pointer-events-none absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-slate-950/95 via-slate-950/65 to-transparent" />
                  <div className="pointer-events-none absolute z-10 text-white" style={storyHeadlineStyle}>
                    <div className="font-bold leading-tight" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                      {storyCopy.visual_headline || storyCopy.guide_title}
                    </div>
                  </div>
                  <div className="pointer-events-none absolute z-10 font-bold leading-tight" style={storyCtaStyle}>{storyCopy.visual_cta}</div>
                  <SafeMarginOverlay assetId="instagram_story" />
                </div>
              ) : (
                <div className="relative flex h-full max-w-full items-center justify-center aspect-[4/5]">
                  <canvas ref={(node) => { canvasRefs.current[spec.id] = node; }} className="max-h-full max-w-full shadow-md" />
                  {spec.id === 'instagram_feed' && <SafeMarginOverlay assetId="instagram_feed" />}
                </div>
              )}
              {renderingAssetIds.includes(spec.id) && <div className="absolute inset-0 flex items-center justify-center bg-slate-900/45 text-xs font-semibold text-white">Updating preview...</div>}
            </div>
            <div className="p-4">
              <h4 className="font-semibold text-slate-900">{spec.label}</h4>
              <p className="mt-0.5 text-xs text-slate-500">{spec.width} × {spec.height} {spec.id === 'onsite_qr'
                ? 'A5 portrait PNG'
                : spec.id === 'instagram_story'
                ? `${storyVideoUrl ? 'uploaded-video' : 'Ken Burns photo'} export${storyAudioUrl ? ` · soundtrack: ${storyAudioLabel || 'approved story'}` : ` · ${Number(config.video_duration_seconds) || 15}s without narration`}`
                : 'PNG'}</p>
              <div className="mt-3 flex gap-2">
                <button
                  type="button"
                  disabled={spec.id === 'instagram_story' ? !storyVideoUrl && !assets[spec.id] : !assets[spec.id]}
                  onClick={() => setMaximizedAssetId(spec.id)}
                  className="flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-slate-300 bg-white px-2 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-100 disabled:bg-slate-100 disabled:text-slate-400"
                ><Maximize2 className="h-3.5 w-3.5" />Maximise</button>
                {spec.id === 'instagram_story' ? (
                  <button
                    type="button"
                    disabled={isRenderingStoryVideo || (!storyVideoFile && !storyVideoStoragePath && !storyImageSource)}
                    onClick={renderAndDownloadStoryVideo}
                    className="flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-slate-900 px-2 py-2 text-xs font-semibold text-white disabled:bg-slate-300"
                  ><Download className="h-3.5 w-3.5" />{isRenderingStoryVideo
                      ? storyVideoProgress < 60 ? `Uploading ${storyVideoProgress}%` : 'Rendering MP4...'
                      : 'Download video'}</button>
                ) : (
                  <button
                    type="button"
                    disabled={!assets[spec.id]}
                    onClick={() => triggerDownload(assets[spec.id], `${safeFilename(config.guide_title)}_${spec.id}.png`)}
                    className="flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-slate-900 px-2 py-2 text-xs font-semibold text-white disabled:bg-slate-300"
                  ><Download className="h-3.5 w-3.5" />{spec.id === 'onsite_qr' ? 'Download A5 PNG' : 'PNG'}</button>
                )}
              </div>
              {assets[spec.id] && <div className="mt-2 flex items-center gap-1 text-xs text-emerald-700"><Check className="h-3.5 w-3.5" />Ready and included in ZIP</div>}
            </div>
          </article>
        ))}
      </div>

      </details>
      </div>

      {maximizedSpec && createPortal((
        <div
          role="dialog"
          aria-modal="true"
          aria-label={`${maximizedSpec.label} maximised preview`}
          className="fixed z-[200] flex flex-col overflow-hidden bg-slate-950/95 backdrop-blur-sm"
          style={{
            left: `${maximizedViewport.offsetLeft}px`,
            top: `${maximizedViewport.offsetTop}px`,
            width: `${maximizedViewport.width || window.innerWidth}px`,
            height: `${maximizedViewport.height || window.innerHeight}px`,
          }}
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setMaximizedAssetId('');
          }}
        >
          <div ref={maximizedHeaderRef} className="flex shrink-0 items-center justify-between gap-4 border-b border-white/10 px-4 py-3 text-white sm:px-6">
            <div>
              <div className="font-semibold">{maximizedSpec.label}</div>
              <div className="mt-0.5 text-xs text-slate-400">{maximizedSpec.width} × {maximizedSpec.height}</div>
            </div>
            <button type="button" onClick={() => setMaximizedAssetId('')} aria-label="Close maximised preview" className="rounded-full border border-white/20 bg-white/10 p-2 text-white hover:bg-white/20"><X className="h-5 w-5" /></button>
          </div>
          <div className="flex min-h-0 flex-1 items-center justify-center overflow-hidden p-3 sm:p-5">
            {maximizedSpec.id === 'instagram_story' && storyVideoUrl ? (
              <video src={storyVideoUrl} controls autoPlay playsInline style={maximizedPreviewSize || undefined} className="max-h-full max-w-full object-contain shadow-2xl" />
            ) : maximizedPreviewUrl ? (
              <img src={maximizedPreviewUrl} alt={`${maximizedSpec.label} maximised preview`} style={maximizedPreviewSize || undefined} className="max-h-full max-w-full object-contain shadow-2xl" />
            ) : (
              <div className="text-sm text-slate-300">Preparing preview…</div>
            )}
          </div>
        </div>
      ), document.body)}
    </section>
  );
};

export default MediaAssetStudio;
